/**
 * Dicionário de textos em inglês.
 *
 * Mesma árvore de `pt-BR.ts`, galho por galho: é o `satisfies Dicionario` do fim
 * do arquivo que cobra isso. Chave a mais, chave a menos ou texto onde deveria
 * haver galho vira erro de compilação aqui, e não texto faltando na tela de
 * quem está jogando.
 *
 * Não é tradução palavra por palavra, é futebol em inglês: `rodada` é
 * `matchday`, `elenco` é `squad`, `clássico` é `derby`, `artilharia` é
 * `top scorers`. Onde a frase em inglês pede outra ordem, os marcadores
 * `{assim}` mudam de lugar dentro dela; o que não pode mudar é o nome deles,
 * que é o que a tela manda.
 *
 * Inglês costuma ocupar menos espaço que o português, mas nem sempre: rótulo de
 * coluna vai curto de propósito, porque a tabela foi desenhada com a largura do
 * português.
 */
import type { Dicionario } from "./nucleo";

export const en = {
  // -------------------------------------------------------------------------
  // Comum a várias telas
  // -------------------------------------------------------------------------
  tabela: {
    buscar: "Search",
    ordenarPor: "Sort by {coluna}",
    nadaEncontrado: "Nothing found.",
  },

  comum: {
    marcaLinha1: "LEGACY",
    marcaLinha2: "FOOTBALL MANAGER",
    /** O nome do produto inteiro, para o emblema de quem usa leitor de tela. */
    marca: "Legacy Football Manager",

    fechar: "Close",
    cancelar: "Cancel",
    continuar: "Continue",
    voltar: "Back",
    naFila: "queued",
    vazio: "-",
    semClube: "Free agent",
    clubeDesconhecido: "another club",
    condicao: "Condition",
    estrelas: "{marcadas} of {total} stars",
    moral: "Morale {valor}",
    semJogos: "No matches yet",
    pontos: "points",

    caracteristicas: {
      arm: "Playmaking",
      cab: "Heading",
      cru: "Crossing",
      des: "Tackling",
      dri: "Dribbling",
      fin: "Finishing",
      mar: "Marking",
      pas: "Passing",
      res: "Stamina",
      vel: "Pace",
      col: "Positioning",
      sai: "Rushing out",
      ref: "Reflexes",
      pen: "Penalties",
    },

    setores: {
      GOL: "Goalkeepers",
      DEF: "Defenders",
      MEI: "Midfielders",
      ATA: "Forwards",
    },

    /*
     * Dinheiro em inglês é `$1.2M`, sem espaço e com a letra colada no número,
     * e o sinal é o menos e não a palavra. É a forma que todo mundo lê sem
     * pensar, e ela cabe em menos espaço que a portuguesa.
     */
    dinheiro: {
      negativo: "-",
      bilhoes: "{sinal}${valor}B",
      milhoes: "{sinal}${valor}M",
      milhares: "{sinal}${valor}K",
      unidade: "{sinal}${valor}",
    },

    /** O inglês põe o mês antes do dia. Os marcadores trocam de lugar, e pronto. */
    data: {
      rodada: "{mes} {dia}, {ano} · {diaDaSemana}",
      diasDaSemana: {
        domingo: "Sunday",
        segunda: "Monday",
        terca: "Tuesday",
        quarta: "Wednesday",
        quinta: "Thursday",
        sexta: "Friday",
        sabado: "Saturday",
      },
      meses: {
        jan: "Jan",
        fev: "Feb",
        mar: "Mar",
        abr: "Apr",
        mai: "May",
        jun: "Jun",
        jul: "Jul",
        ago: "Aug",
        set: "Sep",
        out: "Oct",
        nov: "Nov",
        dez: "Dec",
      },
    },

    tempo: {
      agora: "just now",
      minutos: "{valor} min ago",
      horas: "{valor} h ago",
      dias: "{valor} d ago",

      acabou: "deadline passed",
      contagemDias: "{dias}d {horas}h",
      contagemHoras: "{horas}h {minutos}min",
      contagemMinutos: "{minutos}min {segundos}s",
    },
  },

  // -------------------------------------------------------------------------
  // Modo online, o que vale em qualquer tela dele
  // -------------------------------------------------------------------------
  online: {
    carregando: "Loading...",
    /*
     * O servidor manda a mensagem de erro pronta, e hoje ela sai em português
     * qualquer que seja o idioma do jogo. Este texto cobre só o que não veio da
     * API, como uma falha dentro do próprio navegador.
     */
    erroInesperado: "Something went wrong. Try again.",
    tentarDeNovo: "Try again",
    conectado: "connected",
    reconectando: "reconnecting...",
    precisaAtualizar:
      "The server speaks a different version of the protocol. Update the game to get back online.",
  },

  // -------------------------------------------------------------------------
  // Sessão de jogo solo
  // -------------------------------------------------------------------------
  sessao: {
    semJogo: "No game in progress.",
    semClubeEscolhido: "No club picked.",
    semClubeValido: "No valid club to take over.",
    treinadorForaDaPartida: "That manager is not in this game.",
    saveNaoEncontrado: "Save not found.",
    semArmazenamento: "This browser will not let the game save.",
    armazenamentoRecusou: "The browser refused to store the save.",
    naoSalvou: "Could not save. The game goes on, but your progress is not stored.",
    encerrada: "The game session has ended. Reload the page.",
    motorParou: "The engine stopped responding.",
    falhaInesperada: "Something went wrong in the engine: {detalhe}",
    arquivoNaoServe: "This file will not do. {problemas}",

    temporadaEncerrada: "Season over. Move on to pre-season.",
    rodadaSemJogo: "Round {rodada} played. Your side had no match this round.",

    semPartidaAoVivo: "There is no match in progress.",
    semPartidaNaRodada: "There is no live match this round.",
    trocaImpossivel: "That substitution is not possible right now.",

    janelaFechada: "The transfer window is closed.",
    jogadorJaEhSeu: "This player is already yours.",
    elencoNoLimite: "The squad is already at the {maximo}-player limit.",
    naoDesceDePatamar: "{jogador} would not drop that far down.",
    salarioAbaixoDoPedido: "{jogador} wants at least {salario} per round.",
    caixaNaoCobreEntrada:
      "Your cash will not cover this down payment on top of the offers already queued.",
    propostaMuitoBaixa: "A bid that low would be turned down on the spot.",
    compraIndisponivel: "{jogador} can't be bought outright.",
    semInteressados: "No club wants {jogador} right now.",
    caixaNaoCobreCompra: "Your cash doesn't cover the purchase plus the offers already queued.",
    propostaFechada: "This offer is no longer open.",
    propostaDeOutroClube: "This offer is not yours to answer.",
    coletivaFechada: "That press conference is already over.",

    olheiroNoProprioElenco: "You do not need a scout for your own squad.",
    caixaNaoCobreOlheiro: "Your cash will not cover the scout on top of what is already queued.",
    olheiroJaNaFila: "That scouting trip is already queued.",

    jogadorDeOutroClube: "This player is not yours.",
    acaoDesconhecida: "Unknown action.",

    garotoForaDaBase: "That youngster is not in your academy.",
    garotoNoLimiteDeIdade: "{jogador} cannot stay in the academy past {idade}.",

    ingressoForaDaFaixa: "The ticket price has to be between {minimo} and {maximo}.",
    bancoLiberaAte: "The bank will lend {valor} at most.",
    caixaNaoCobrePagamento: "Your cash will not cover that payment.",
    obraEmAndamento: "Finish the current work before starting another.",
    estruturaDesconhecida: "Unknown facility.",
    obraUmNivelPorVez: "Facilities go up one level at a time.",
    caixaNaoCobreObra: "Your cash will not cover the work on top of what is already queued.",

    fila: {
      escalacaoDefensiva: "Line-up in {formacao}, defensive style",
      escalacaoEquilibrada: "Line-up in {formacao}, balanced style",
      escalacaoOfensiva: "Line-up in {formacao}, attacking style",
      proposta: "Bid for {jogador}",
      aceitarProposta: "Accept the bid for {jogador}",
      recusarProposta: "Reject the bid for {jogador}",
      elogiar: "Praise {jogador}",
      criticar: "Have a word with {jogador}",
      renovar: "Renew {jogador}'s contract",
      dispensar: "Release {jogador}",
      listar: "List {jogador} for transfer",
      tirarDaLista: "Take {jogador} off the list",
      observar: "Scout {jogador}",
      emprestimo: "Loan {jogador} in, covering {parte}% of the wages",
      promover: "Promote to the first team: {jogador}",
      manter: "Keep another year in the academy: {jogador}",
      dispensarDaBase: "Release from the academy: {jogador}",
      venderDaBase: "Sell from the B team: {jogador}",
      trocarTecnicoDaBase: "Replace the B team coach",
      ingresso: "Ticket price at {valor}",
      emprestimoNoBanco: "Take a bank loan",
      abaterDivida: "Pay down the debt",
      obra: "Start building work",
      clube: "Club adjustment",
      jogadorDesconhecido: "the player",
      garotoDesconhecido: "the youngster",
      coletiva: "Answer the press conference",
      coletivaSemComentarios: "Press conference: no comment",
    },
  },

  // -------------------------------------------------------------------------
  // Início
  // -------------------------------------------------------------------------
  home: {
    lema: "Write your story. Leave your legacy.",
    continuarCom: "Continue with {clube}",
    novaCarreira: "New career",
    bancoProprio: "Start with your own database",
    editorDeBanco: "Database editor",
    erroDeArquivo: "I could not read that file. It has to be valid JSON.",
    carreirasSalvas: "Load game",
    importarSave: "Import .lmsave file",
    exportarSave: "Export",
    saveImportado: "Career imported",
    erroDeSave: "That file is not a .lmsave career.",
    resumoDoSave: "{treinador} · season {temporada}, matchday {rodada}/{total} · {quando}",
    licenca: {
      teste: "Free trial · {dias} days",
      ultimoDia: "Free trial · last day",
      encerrado: "Trial ended",
      testeUsado: "Trial already used on this device",
      relogio: "Check this device's date and time",
      comprar: "Buy the game",
    },
    apagar: "Delete",
    modo: {
      umJogador: "Single Player",
      online: "Online Multiplayer",
      trocar: "Switch mode",
    },

    online: {
      procurando: "Looking for your career.",
      comecar: "Start a career",
      continuar: "Continue with {clube}",
      resumo: "{treinador} · season {temporada}, matchday {rodada} · {quando}",
      exportar: "Export for Single Player",
      apagar: "Delete career",
      apagarConfirma: "Delete this account's career? There is no undo.",
    },


    versao: "alpha · version {versao}",
  },

  // -------------------------------------------------------------------------
  // Nova carreira
  // -------------------------------------------------------------------------
  novoJogo: {
    gerandoRotulo: "Building the world",
    gerandoTitulo: "Creating countries, divisions, clubs and squads",
    gerandoCorpo: "Over 8,000 players, each with a name, a contract and a story.",

    rotulo: "New career",
    titulo: "Pick your club",
    resumoDaGeracao: "{clubes} clubs generated in {ms} ms.",
    seuNome: "Your name",
    pais: "Country",
    divisao: "Division",
    divisaoOpcao: "Division {numero}",
    colunas: {
      clube: "Club",
      reputacao: "Reputation",
      elenco: "Squad",
      caixa: "Cash",
    },
    tambemVaoJogar: "Also managing",
    assento: "{treinador} · {clube}",
    tirar: "remove",
    selecione: "Pick a club from the list.",
    clubeEscolhido: "{clube}, {liga}",
    adicionarTreinador: "Add another manager",
    adicionarTreinadorDica: "Play with friends on the same device",
    comecarComTreinadores: "Start with {total} managers",
    assumirOComando: "Take charge",
  },

  // -------------------------------------------------------------------------
  // Criador de clube, dentro da nova carreira
  // -------------------------------------------------------------------------
  criador: {
    titulo: "New club",
    escudo: "Crest",

    nome: {
      rotulo: "Club name",
      bloqueado: "Name not allowed.",
      duvidoso: "Name flagged for review.",
    },

    sigla: {
      // `sigla` é `short name` em todo o resto do jogo, inclusive no editor de
      // banco. Vale manter a mesma palavra, que é a que aparece na tabela.
      rotulo: "Short name",
      jaExiste: "Short name already taken in this world.",
      formato: "Three letters, A to Z.",
      sugestoes: "Free",
      bloqueado: "Short name not allowed.",
      duvidoso: "Short name flagged for review.",
    },

    forma: "Shape",
    formas: {
      shield: "Shield",
      circle: "Circle",
      diamond: "Diamond",
      pennant: "Pennant",
    },

    padrao: "Pattern",
    padroes: {
      solid: "Plain",
      sash: "Sash",
      stripes: "Stripes",
      halves: "Halves",
    },

    cores: {
      titulo: "Colours",
      fundo: "Background",
      segunda: "Second",
      contorno: "Outline",
    },

    catalogo: {
      titulo: "Icons",
      abas: {
        cheios: "Filled",
        contornos: "Outlined",
      },
      busca: "Search icons",
      nadaEncontrado: "Nothing found.",
      todas: "All",
      categorias: {
        animais: "Animals",
        armas: "Weapons",
        esporte: "Sport",
        heraldica: "Heraldry",
        maquina: "Machines",
        natureza: "Nature",
        simbolos: "Symbols",
      },
    },

    peca: {
      titulo: "Pieces",
      adicionar: "Add",
      remover: "Remove",
      paraFrente: "Bring forward",
      paraTras: "Send back",
      girar: "Rotate",
      tamanho: "Size",
      cor: "Colour",
      contagem: "{total} of {maximo}",
      limite: "Up to {maximo} pieces.",
      vazio: "No pieces",
    },

    previa: {
      titulo: "Preview",
      tabela: "Table",
      partida: "Match",
      cabecalho: "Header",
      grande: "Large",
    },

    uniforme: {
      titulo: "Kit",
      padrao: "Pattern",
      padroes: {
        solid: "Plain",
        stripes: "Stripes",
        sash: "Sash",
        hoops: "Hoops",
        halves: "Halves",
        checks: "Checks",
      },
      cores: "Colours",
      corPrincipal: "Main",
      corSecundaria: "Second",
      peito: "Piece on the chest",
      semPeca: "No piece",
    },

    confirmar: "Create club",
    escudoDefinitivo: "The crest cannot be changed later.",

    creditos: {
      titulo: "Art credits",
      catalogo: "Catalogue",
      autor: "Author",
      licenca: "Licence",
      usadas: "Icons used",
      figurasUsadas: "{total} icons",
    },
  },

  // -------------------------------------------------------------------------
  // Hub do clube, incluindo a coluna de painéis da direita
  // -------------------------------------------------------------------------
  hub: {
    navAria: "Game sections",
    reputacaoTecnico: {
      elite: "Elite",
      alta: "High",
      boa: "Good",
      regular: "Fair",
      baixa: "Low",
    },
    sair: "Leave career",
    nav: {
      elenco: "Squad",
      tatica: "Tactics",
      mercado: "Transfers",
      competicoes: "Competitions",
      clube: "Club",
      financas: "Finances",
      noticias: "News",
      carreira: "Career",
      base: "Youth",
      /* O botão que abre a folha no celular. */
      clubeFinancas: "Club / Finances",
      calendario: "Calendar",
      timeB: "B Team",
      relatorios: "Reports",
      configuracoes: "Settings",
      mais: "More",
    },
    abrirDados: "Show the club data",
    fecharDados: "Hide the club data",
    fatos: {
      moral: "Morale",
      pontos: "Points",
      campanha: "W-D-L",
      treinador: "Manager",
      reputacaoTecnico: "Reputation",
      estadio: "Stadium",
      fundacao: "Founded",
      caixa: "Cash",
      objetivo: "Target",
    },
    lugares: "{capacidade} seats",
    preTemporada: "Preseason {temporada}",
    temporadaRodada: "Season {temporada} · matchday {rodada}/{total}",
    vezDe: "turn",


    decisoesNaFila: "{total} decision(s) queued",
    atalhosDica: "Keyboard shortcuts",
    atalhosAria: "See the keyboard shortcuts",
    simulando: "Simulating...",
    proximaTemporada: "Start next season",
    jogarRodada: "Play matchday {rodada}",
    jogarFase: "Play: {fase}",
    avancarRodada: "Next matchday",
    filaTitulo: "Queued for the next matchday ({total})",
    filaVazia: "No decisions queued.",

    temporadaEncerrada: "Season over.",
    semJogoMarcado: "No match scheduled.",
    versus: "vs",
    emCasa: "Home",
    foraDeCasa: "Away",
    competicaoRodada: ", matchday {rodada}",
    competicaoFase: ", {fase}",
    classico: "Derby",

    desempenho: "Season so far",
    posicao: "Position",
    /** Sem ordinal: `{posicao}º` viraria 1st, 2nd, 3rd, e o dicionário não conta. */
    posicaoValor: "#{posicao}",
    jogosDisputados: "{jogos} match(es) played",
    vitorias: "Wins",
    empates: "Draws",
    derrotas: "Losses",
    saldo: "Goal difference",
    ultimosJogos: "Recent form",

    financas: "Cash",
    saldoEmCaixa: "Cash balance",
    receitaPorRodada: "Income per matchday",
    despesaPorRodada: "Costs per matchday",
    salarios: "Wages",
    moralExcelente: "Excellent",
    moralBoa: "Good",
    moralRegular: "Average",
    moralRuim: "Poor",
    moralValor: "{rotulo} ({valor})",

    rivalidades: "Rivalries",
  },

  // -------------------------------------------------------------------------
  // Elenco
  // -------------------------------------------------------------------------
  elenco: {
    titulo: "First team",
    escalacao: {
      titular: "Starter",
      reserva: "Sub",
      lesionado: "Injured · {rodadas}",
      suspenso: "Suspended · {rodadas}",
      fora: "Out",
    },
    colunas: {
      tipo: "Type",
      escalacao: "Lineup",
      numero: "No.",
      pos: "Pos",
      jogador: "Player",
      forca: "Str",
      moral: "Morale",
      fisico: "Fit",
      gols: "Goals",
      situacao: "Status",
      salario: "Wages",
    },
    lesionado: "injured {rodadas}",
    suspenso: "suspended",
    linhaSecundaria: "{idade} yrs · {destaques}",
    aVendaNinguem: "For sale · no one interested",
    aVendaUm: "For sale · 1 interested",
    aVendaVarios: "For sale · {n} interested",
    titular: "Starter",
    banco: "Bench",
    fora: "Out",
  },

  // -------------------------------------------------------------------------
  // Youth academy
  // -------------------------------------------------------------------------
  base: {
    titulo: "Youth academy",
    turma: "{total} in the academy",
    explicacao:
      "These are the kids coming through. Their ceiling is hidden, as it is everywhere else in the game: what you see is the range your staff believes in. At 18, each one becomes your call: step up, one more year, or out.",
    estrutura: "Academy level {nivel}",
    oQueONivelCompra:
      "The academy level decides how many kids fit here, how good they arrive and how narrow the potential range is.",
    vazia: "Nobody in the academy yet. The next intake arrives when the season turns.",

    decisaoTitulo: "Waiting on your call",
    decisaoDica: "Queued, and takes effect when you advance",
    promover: "Promote",
    manter: "Keep another year",
    dispensar: "Release",
    prazo: "Can stay in the academy through season {temporada}.",

    naBase: "In the academy",
    esperandoTotal: "{total} waiting on you",
    emFormacao: "Still developing",
    emFormacaoTotal: "{total} not old enough to decide",
    emFormacaoRotulo: "Developing",
    esperaDecisao: "Awaiting call",
    parado: "Stopped growing",
    naFilaPromover: "Promote, queued",
    naFilaManter: "Keep, queued",
    naFilaDispensar: "Release, queued",

    anos: "{idade} yrs",
    faixa: "{baixo} to {alto}",
    potencialEstimado: "estimated potential",
    evolucaoValor: "+{pontos}",

    colunas: {
      pos: "Pos",
      garoto: "Player",
      idade: "Age",
      forca: "Str",
      evolucao: "Growth",
      faixa: "Potential",
      situacao: "Status",
    },

    veredito: {
      vaiCrescer: "Plenty of room to grow, and worth the bet.",
      quaseFeito: "Still improving, but close to the finished article.",
      noTeto: "He is at his ceiling. What you see is what you get.",
      estagnou: "A year went by with nothing to show, and his ceiling shrank with it.",
    },

    confianca: {
      alta: "high confidence",
      media: "medium confidence",
      baixa: "low confidence",
    },
  },

  // -------------------------------------------------------------------------
  // Tática
  // -------------------------------------------------------------------------
  // Outro clube visto de fora, na ficha que abre ao tocar nele.
  clubeDeFora: {
    aria: "{nome} squad",
    abrir: "See the {nome} squad",
    timeProvavel: "Likely lineup",
    elenco: "Squad",
    idade: "Age",
    titular: "In the likely lineup",
  },
  tatica: {
    escalacao: "Lineup",
    forcaEmCampo: "on-pitch strength {valor}",
    automatica: "Auto pick",
    formacao: "Formation",
    personalizada: "Custom",
    capitao: "Captain",
    vagaAberta: "Empty slot",
    improvisado: "out of position ({pos})",
    naPosicao: "in position",
    condicaoDoTitular: "· {condicao}%",

    bancoDeReservas: "Substitutes ({total})",
    nenhumReserva: "Nobody on the bench.",
    foraDosRelacionados: "Not in the squad list",

    postura: "How the team plays",
    estilo: "Style",
    estiloDefensivo: "Defensive",
    estiloDefensivoAjuda: "Protects the result. Less attack, more defence.",
    estiloEquilibrado: "Balanced",
    estiloEquilibradoAjuda: "No tilt either way.",
    estiloOfensivo: "Attacking",
    estiloOfensivoAjuda: "Goes for it. Creates more and concedes more.",
    marcacao: "Tackling",
    marcacaoLeve: "Soft",
    marcacaoLeveAjuda: "Fewer fouls and cards, a looser defence.",
    marcacaoNormal: "Normal",
    marcacaoNormalAjuda: "The default.",
    marcacaoForte: "Hard",
    marcacaoForteAjuda: "A better defence, but it tires the team and draws cards.",
    ritmo: "Tempo",
    ritmoBaixo: "Low",
    ritmoBaixoAjuda: "Saves the legs at the cost of a little output.",
    ritmoNormal: "Normal",
    ritmoNormalAjuda: "The default.",
    ritmoAlto: "High",
    ritmoAltoAjuda: "More in the first half. Fades in the second if the legs go.",

    penaltis: "Penalties",
    faltas: "Free kicks",
    escanteios: "Corners",
    automatico: "Automatic",

    avisoDaFila: "The lineup is queued and takes effect from the next matchday.",
    confirmar: "Confirm lineup",

    escolherPara: "Pick a player for {pos}",
    quemJogaAqui: "Who plays here",
    idadeDoCandidato: "{idade} yrs",
    fisicoDoCandidato: "{condicao}% fit",
    jaTitular: "already a starter, will swap slots",
    candidatoLesionado: "injured",
    candidatoSuspenso: "suspended",

    forca: "Strength",
    confirmarCurto: "Confirm",
    confirmada: "Confirmed",
    reservas: "Substitutes",
    abaBanco: "Bench",
    lugarVazio: "Empty spot",
    legenda: "Legend",
    legCondicao: "Fitness",
    legImprovisado: "Out of position",
    legCansado: "Tired",
    legPendurado: "One booking away",
    legLesionado: "Injured",
    legSuspenso: "Suspended",
    setorAta: "Att",
    setorMei: "Mid",
    setorDef: "Def",
    naVaga: "In slot",
    condicaoCurta: "Fit.",
    moral: "Morale",
    media: "Avg",
    golsAssist: "G / A",
    posicaoEIdade: "{pos} · {idade} yrs",
    naturalEm: "{natural} at {vaga}",
    soltar: "Drop",
    adversario: "Opponent",
    posicaoEPontos: "{posicao} · {pontos} pts",
    dueloAtaque: "Attack v Def",
    dueloMeio: "Mid v Mid",
    dueloDefesa: "Defence v Att",
    destaques: "Key players",
    desfalques: "Unavailable",
    destaqueGols: "{valor} goals",
    destaqueAssist: "{valor} assists",
    destaqueCapitao: "captain",
    tituloLesionado: "Injured: {rodadas} rounds",
    tituloSuspenso: "Suspended: {rodadas} rounds",
    tituloImprovisado: "Out of position, natural {pos}",
    tituloCansado: "Fitness {valor}%",
    tituloPendurado: "One booking away: {amarelos} yellows",
    rotuloDaFicha: "{nome}, {pos}, rating {nota}, fitness {condicao}%",
  },

  // -------------------------------------------------------------------------
  // Mercado
  // -------------------------------------------------------------------------
  mercado: {
    janelaFechada:
      "The transfer window is closed. You can still look around and build a shortlist, but not sign anyone.",
    reabreNaRodada: "Reopens on matchday {rodada}.",
    reabreNaPreTemporada: "Reopens in preseason.",

    propostasRecebidas: "Offers received ({total})",
    semPropostasRecebidas: "No offers received.",
    propostasEnviadas: "Offers sent ({total})",

    procurarJogador: "Search for a player",
    encontrados: "{total} found",
    paginas: "Pages",
    primeiraPagina: "First page",
    paginaAnterior: "Previous page",
    proximaPagina: "Next page",
    ultimaPagina: "Last page",
    paginaDe: "{pagina} of {paginas}",
    nomeDoJogador: "Player name",
    todasAsPosicoes: "All positions",
    qualquerIdade: "Any age",
    ateAnos: "up to {idade} yrs",
    qualquerForca: "Any strength",
    forcaOuMais: "strength {valor} or more",
    soOQueCabe: "Only what the cash covers",
    abaProcurar: "Search",
    abaLivres: "Free agents",

    colunas: {
      pos: "Pos",
      jogador: "Player",
      clube: "Club",
      forca: "Str",
      preco: "Price",
      pede: "Wants",
    },
    naLista: "listed",
    linhaSecundaria: "{idade} yrs · {destaques}",
    livre: "free",
    naoVem: "not for sale",
    nenhumJogador: "No player matches those filters.",

    jogadorResumo: "{pos}, strength {forca}",
    ofertaRecebida: "Bid from {clube} for {valor}",
    ofertaEnviada: "Bid to {clube} for {valor}",
    respondeEm: "replies within {rodadas} matchday(s)",
    aceitaNaFila: "Accepted · queued",
    recusadaNaFila: "Declined · queued",
    recusar: "Reject",
    aceitar: "Accept",
    aguardando: "waiting",
  },

  // -------------------------------------------------------------------------
  // Competições
  // -------------------------------------------------------------------------
  competicoes: {
    abaEstadual: "State",
    abaCopa: "Cup",
    abaContinental: "Continental",
    abaArtilharia: "Top scorers",
    abaCalendario: "Fixtures",
    faixaDeConfrontos: "{de}–{ate} of {total}",
    confronto: {
      resultado: "Result",
      rodada: "Matchday",
      local: "Venue",
      naTabela: "In the table",
      outrosJogos: "Other games",
      rodadaCurta: "M{rodada}",
    },

    campeao: "Champion: {clube}",
    estadualResumo: "opens the season and brings the state's divisions together",

    calendario: {
      resumo: "{jogos} matches this season, {classicos} of them derbies",
      rodada: "Matchday {numero}",
      casa: "Home",
      fora: "Away",
      neutro: "Neutral ground",
      proximo: "Next",
      vazio: "No match scheduled for this season.",
      todos: "All",
      filtroAjuda: "Filter by competition to find the match you care about.",
    },

    /** Siglas de tabela em inglês: Played, Won, Drawn, Lost, GF, GA, GD, Pts. */
    colunas: {
      posicao: "#",
      clube: "Club",
      jogos: "P",
      vitorias: "W",
      empates: "D",
      derrotas: "L",
      golsPro: "GF",
      golsContra: "GA",
      saldo: "GD",
      pontos: "Pts",
    },

    seuGrupo: "your group",
    tituloDaEdicao: "{clube} won this edition.",
    doisPrimeiros:
      "The top two in each group go through to the knockout rounds. The green band marks who would qualify today.",
    gruposEncerrados: "Group stage over. The title is settled in the knockout rounds.",
    mostrarSoMeuGrupo: "Show only my group",
    verOutrosGrupos: "See the other {total} groups",
    esconderGrupos: "Hide the group stage",
    verGrupos: "See the group stage",

    chaveamento: "Bracket",
    copaNaoComecou:
      "The cup has not started yet. The first-round draw comes when the matchday arrives.",
    campanha: {
      naoClassificou: "Your club did not qualify for this edition.",
      grupos: "In the group stage.",
      gruposComMeu: "In the group stage, in {grupo}.",
      eliminadoNosGrupos: "Knocked out in the group stage.",
      campeao: "{competicao} winners.",
      tituloDeOutro: "Knocked out. The title went to {clube}.",
      vivo: "Still in the cup. Current round: {fase}.",
      vivoSemFase: "Still in the cup.",
      eliminado: "Knocked out. Last round: {fase}.",
      foraDaCopa: "Has not entered the cup yet.",
    },
    idaEVolta: "two legs",
    jogoUnico: "single leg",
    emDisputa: "in progress",
    maisConfrontos: "and {total} more ties",
    ida: "first leg",
    aDefinir: "To be decided",
    aDecidir: "to be decided",
    penaltis: "penalties",
    agregado: "aggregate",

    artilharia: "Top scorers",
    ninguemMarcou: "Nobody has scored this season yet.",
    artilhariaDa: "{liga} top scorers",
    quantosNaLista: "{total} on the list",
    colunasArtilharia: {
      posicao: "#",
      pos: "Pos",
      jogador: "Player",
      clube: "Club",
      jogos: "Apps",
      assistencias: "Ast",
      gols: "Goals",
    },
  },

  // -------------------------------------------------------------------------
  // Clube
  // -------------------------------------------------------------------------
  clube: {
    precoDoIngresso: "Ticket price",
    explicacaoDoPreco:
      "Charge too much and the ground empties, taking the gate with it. The numbers below are the crowd and the revenue expected against an average opponent, from the same maths the game runs on matchday.",
    estadioLota:
      "The ground sells out at today's price. You could charge up to {preco} without losing anyone, which would be {ganho} more per home match.",
    colunas: {
      ingresso: "Ticket",
      publico: "Expected crowd",
      ocupacao: "Occupancy",
      renda: "Revenue per match",
    },
    atual: "current",
    melhorRenda: "best revenue",
    cobrar: "Charge",

    estruturas: "Facilities",
    // What each facility changes. It used to be hard-coded in
    // `competition-views.ts`, showing up in Portuguese under a facility name
    // that was already translated.
    efeitoDaEstrutura: {
      stadium: "Ground capacity and comfort. It moves the crowd and the gate.",
      youth:
        "How many youngsters the academy holds, how good they arrive and how narrow a potential range your staff can see.",
      training: "How fast the squad improves and how quickly it recovers between matchdays.",
      medical: "Cuts the time a player spends out after an injury.",
    },
    obraEmAndamento: "Work under way",
    obraDescricao: "{nome} going up to level {nivel}. {rodadas} matchday(s) to go.",
    manutencao: "upkeep {valor} per matchday",
    noMaximo: "maxed out",
    rodadasDeObra: "{rodadas} matchdays of work",
    subirPara: "Upgrade to {nivel}",
    nivelAria: "level {nivel} of 5",

    parcelas: "Transfer installments",
    parcelasResumo: "{aPagar} to pay · {aReceber} to receive",
    colunasParcelas: {
      jogador: "Player",
      fluxo: "Flow",
      venceEm: "Due",
      valor: "Amount",
    },
    aReceber: "to receive",
    aPagar: "to pay",
    nestaRodada: "this matchday",
    emRodadas: "{rodadas} matchday(s)",

    banco: "Bank",
    caixa: "Cash",
    divida: "Debt",
    credito: "Credit available",
    juros: "Interest per matchday",
    jurosValor: "{taxa}%",
    explicacaoDoBanco:
      "The bank charges interest every matchday on what you owe. Stay in the red for too long and the board sells a player without asking anyone.",
    descontoDeParcelas:
      "The credit already sets aside {valor} for transfer installments coming due.",
    pegarEmprestado: "Borrow",
    pedir: "Request",
    abaterDivida: "Pay down the debt",
    pagar: "Pay",
  },

  // -------------------------------------------------------------------------
  // Finanças
  // -------------------------------------------------------------------------
  financas: {
    titulo: "Cash",
    saldoEmCaixa: "Cash balance",
    receitaPorRodada: "Income per matchday",
    despesaPorRodada: "Costs per matchday",
    resultadoPorRodada: "Net per matchday",
    bilheteria: "Gate receipts this season",
    patrocinio: "Sponsorship this season",
    tv: "TV money this season",
    premiacao: "Prize money this season",
    folha: "Wage bill per matchday",
    dividaComBanco: "Debt with the bank",
    ingresso: "Ticket",
    capacidade: "Stadium capacity",
  },

  // -------------------------------------------------------------------------
  // Notícias
  // -------------------------------------------------------------------------
  noticias: {
    titulo: "News",
    nadaNovo: "Nothing new here.",
    rodada: "matchday {rodada}",
    compartilharContratacao: "Share the signing",
    contextoDoCartao: "Season {temporada} · matchday {rodada}",
    reforcoConfirmado: "Signing confirmed",
  },

  // -------------------------------------------------------------------------
  // Carreira
  // -------------------------------------------------------------------------
  carreira: {
    /*
     * O que a carreira diz quando a conversa com o servidor falha.
     *
     * Nenhuma destas frases interrompe a partida: a rodada já aconteceu no
     * aparelho e o mundo inteiro sobe no próximo envio.
     */
    servidor: {
      falhaNoEnvio:
        "I could not save your career to the server just now. The matchday still counts and I will try again.",
      semRede:
        "I could not reach the server.",
      downloadIncompleto:
        "The career arrived incomplete.",
      arquivoIlegivel:
        "I could not read the career that came from the server.",
    },

    resumo: "{temporadas} season(s) · {titulos} trophy(ies) · reputation {reputacao}",
    carreiraComeca: "Your career starts now. Finish the first season and it shows up here.",
    colunas: {
      temporada: "Season",
      clube: "Club",
      competicao: "Competition",
      posicao: "Position",
      titulos: "Trophies",
    },
    posicaoValor: "#{posicao}",

    bancoDeDados: "Database",
    explicacaoDoBanco:
      'Save the clubs and the squads of this career to a file. You can fix a name in a text editor, send it to a friend or start another career on the same database. It is the same file the start screen reads in "start with your own database".',
    salvarBanco: "Save database",
    salvamento: "Saving",
    salvarSozinho: "Save automatically every round",
    salvarAgora: "Save now",
    salvo: "Career saved.",

    conquistas: "Achievements",
    conquistasResumo: "{feitas} of {total}",
    temporadaDaConquista: "season {temporada}",
  },

  // -------------------------------------------------------------------------
  // Ficha do jogador
  // -------------------------------------------------------------------------
  ficha: {
    secaoCaracteristicas: "Attributes",
    abaProposta: "Offer",
    abaEmprestimo: "Loan",
    abaQuePesam: "For the position ({total})",
    abaDemais: "Others ({total})",
    temporada: {
      jogos: "Apps",
      gols: "Goals",
      assistencias: "Assists",
      cartoes: "Cards",
    },
    aria: "{nome} profile",
    subtitulo: "{clube} · {idade} yrs · {pe} · {personalidade}",
    fatos: {
      valor: "Value",
      salario: "Wages",
      contratoAte: "Contract until",
      naTemporada: "This season",
      naCarreira: "Career",
    },
    contratoValor: "season {temporada}",
    jogosEGols: "{jogos} apps, {gols} goals",
    moral: "Morale",
    fisico: "Fitness",
    notaMedia: "Average rating",

    olheiro: "Scout",
    confianca: "confidence {valor}",
    laudoResumo: "Potential estimated between {minimo} and {maximo}. {veredito}",
    confiancaAlta: "high confidence",
    confiancaMedia: "medium confidence",
    confiancaBaixa: "low confidence",
    laudoAntigo: "old report",
    faixa: "{baixo} to {alto}",
    potencialEstimado: "estimated potential",
    observarDeNovo: "Scout him again for {valor}",
    semLaudo:
      "You have no idea how far he can go. A scout comes back with a potential range, and the better your youth setup, the tighter that range is.",
    mandarObservar: "Send a scout for {valor}",

    emprestimo: "On loan from {dono} to {tomador}. {tomador} pays {parte}% of the wages.",


    elogiar: "Praise",
    darBronca: "Have a word",
    renovarContrato: "Renew contract",
    tirarDaLista: "Take off the list",
    listarParaVenda: "List for transfer",
    venderAgora: "Sell now for {valor}",
    interessadosNinguem: "No one interested",
    interessadosUm: "1 club interested",
    interessadosVarios: "{n} clubs interested",

    fazerProposta: "Make an offer",
    leilao: "{clubes} clubs are chasing this player. The highest bid is {maiorLance}.",
    leilaoSeuLanceLidera: "Yours is the highest bid so far.",
    leilaoPrecisaCobrir: "To be heard first, you have to beat it.",
    naoDesceDePatamar: "{nome} would not drop that far down. Not even worth trying.",
    explicacaoDaProposta:
      "The club starts listening at {minimo}. He wants at least {salario} per matchday. Your cash is {caixa}.",
    valorDaCompra: "Transfer fee",
    salarioPorRodada: "Wages per matchday",
    anos: "Years",
    parcelas: "Installments",
    aVista: "up front",
    duasVezes: "2 payments",
    tresVezes: "3 payments",
    explicacaoDasParcelas:
      "{entrada} goes out now and the rest in {restantes} payment(s) of {entrada}, every 12 matchdays. The seller discounts a split deal when deciding, so the offer has to be higher to carry the same weight.",
    enviarProposta: "Send offer",
    comprarPor: "Buy for {valor}",
    contratarAgora: "Sign now",
    janelaFechada: "Window closed",

    ouPegarEmprestado: "Or take him on loan",
    explicacaoDoEmprestimo:
      "He plays for you until the end of the season and then goes back to his club. No fee, but you take on {parte}% of the wages, which is {valor} per matchday.",
    pedirEmprestado: "Ask for the loan",
  },

  // -------------------------------------------------------------------------
  // Resumo da partida
  // -------------------------------------------------------------------------
  adm: {
    proximoJogo: "Next match",
    desempenho: "Season form",
    financas: "Finances",
    jogadorSelecionado: "Selected player",
    abrirFicha: "Profile",
    jogos: "P",
    vitorias: "W",
    empates: "D",
    derrotas: "L",
    pontos: "points",
    moralDoClube: "Club morale",
    media: "Average",
    jogosNaTemporada: "Games",
    gols: "Goals",
    assistencias: "Assists",
    amarelos: "Yellows",
    valor: "Value",
    idadeEDestaques: "{idade} years · {destaques}",
  },
  relatorios: {
    abas: {
      posJogo: "Post-match",
      rodada: "Matchday",
      elenco: "Squad",
      financeiro: "Finances",
    },
    semJogo: "No match this season yet.",
    estatisticas: "Stats",
    posse: "Possession",
    chances: "Chances",
    selecaoDaRodada: "Team of the matchday",
    destaques: "In form",
    emQueda: "Struggling",
    fisico: "Fitness issues",
    moral: "Morale",
    moralMedia: "Average morale",
    insatisfeitos: "Unhappy",
    forcaMedia: "Average strength",
    entradas: "Season income",
    saidas: "Per matchday",
    outrasDespesas: "Other expenses",
    bilheteria: "Gate",
    patrocinio: "Sponsorship",
    tv: "TV rights",
    premiacao: "Prize money",
    receita: "Income",
    folha: "Wages",
  },
  saida: {
    titulo: "Leave the career",
    gravadoEm: "Saved · season {temporada}, matchday {rodada}",
    naoGravado: "Not saved yet",
    gravarESair: "Save and quit",
    sairSemGravar: "Quit without saving",
    voltaA: "Back to matchday {rodada}",
  },
  timeB: {
    titulo: "B Team",
    abas: {
      elenco: "B squad",
      partida: "Last match",
      classificacao: "Table",
      historico: "History",
    },
    tecnico: "B team coach",
    moral: "Morale",
    peneira: "Trials",
    peneiraValor: "{nivel} level · pre-season",
    chanceDeEstrela: "Star chance",
    aspirantes: "Reserve League",
    elencoB: "B squad",
    trocarTecnico: "Replace coach",
    ligaDe: "Reserve League · {liga}",
    garoto: "Youngster",
    colunas: {
      jogos: "P",
      gols: "Goals",
      feitos: "Feats",
      tecnico: "Coach",
      estrela: "Star",
      salario: "Wage",
    },
    ultimoAno: "Final year",
    emFormacao: "Developing",
    ovr: "OVR",
    linhaDoGaroto: "{posicao} · {idade} yrs · B team",
    feitos: "Achievements",
    numeros: {
      jogos: "Games",
      gols: "Goals",
      assistencias: "Assists",
      nota: "Avg rating",
    },
    promover: "Promote ↑",
    vender: "Sell",
    dispensar: "Release",
    elencoCheio: "First team is full",
    janelaFechada: "Window closed",
    dispensarConfirma: "{jogador} goes to the free agents.",
    propostas: "Offers received",
    semPropostas: "No offers this matchday",
    aceitar: "Accept",
    recusar: "Decline",
    chutes: "Shots",
    noGol: "On target",
    nomeDoB: "{clube} B",
    ligaERodada: "Reserve League · {liga} · matchday {rodada}",
    lance: {
      gol: "Goal",
      amarelo: "Yellow card",
      vermelho: "Red card",
      lesao: "Injury",
    },
    assistencia: "assist by {jogador}",
    rodadasFora: "{rodadas} md out",
    melhores: "Best on the pitch",
    campeao: "Champions",
    historico: {
      temporada: "Season",
      posicao: "Position",
      pontos: "Pts",
      artilheiro: "Top scorer",
      subiram: "Promoted",
      peneira: "Trials",
    },
    emAndamento: "{posicao} · in progress",
    artilheiroValor: "{jogador} · {gols}",
    historicoDoTimeB: "B team history",
    anoEmAndamento: "{ano} · in progress",
    peneiraDoAno: "{ano} trials",
    chegaram: "{total} arrived · {nivel} level",
    subiramAoPrincipal: "Promoted to the first team",
    atual: "Current",
    contratarTecnico: "Hire",
    multa: "Fee to dismiss {tecnico}",
    promoverTitulo: "Promote to the first team",
    linhaComFeitos: "{posicao} · {idade} yrs · {feitos} achievements",
    estrela: "★ Star",
    jogadorEstrela: "★ Star Player",
    promovido: "Promoted",
    potencial: "Potential",
    moralDoElenco: "Squad morale",
    irAoElenco: "Go to the squad",
  },
  livres: {
    titulo: "Free agents",
    total: "{total} available",
    colunas: {
      origem: "Released by",
      livreHa: "Free for",
      sai: "Leaves",
      pede: "Asks",
    },
    nestaRodada: "this matchday",
    rodadas: "{rodadas} md",
    fimDaTemporada: "end of season",
    emRodadas: "in {rodadas} md",
  },
  partida: {
    gol: "GOAL",
    golContra: "{clube} GOAL",
    numeros: "Numbers",
    noGol: "On target",
    trocas: "Subs",
    sai: "Out",
    entra: "In",
    noBanco: "{n} on the bench",
    condicao: "Fitness {condicao}%",
    semTrocas: "No subs left",
    trocado: "Swapped: {sai} out, {entra} in",
    substituicao: "Substitution",
    encerrada: "Finished",
    voltar: "Back",
    continuarOJogo: "Resume the match",
    primeiroTempo: "1st half",
    segundoTempo: "2nd half",
    ajustado: "Formation {formacao}",
    trocouPosicao: "{a} and {b} swapped positions",
    aria: "Match result",
    vitoria: "Win",
    empate: "Draw",
    derrota: "Loss",
    classico: "Derby",
    competicaoFase: ", {fase}",
    posse: "Possession {valor}%",
    chances: "Chances {minhas}-{dele}",
    porQueAconteceu: "Why it happened",
    lances: "Key moments",
    minuto: "{minuto}'",
    compartilhar: "Share",
  },

  // -------------------------------------------------------------------------
  // Partida ao vivo
  // -------------------------------------------------------------------------
  aoVivo: {
    expandir: "Expand the match",
    encolher: "Shrink the match",
    abaLances: "Play by play",
    abaRodada: "Round",
    jogoAcabou: "full time",
    aria: "Live match",
    fimDeJogo: "full time",
    minuto: "{minuto}'",
    bolaVaiRolar: "Kick-off is coming.",

    intervalo: "Half-time",
    pausado: "Paused",
    restamTrocas: "{trocas} subs left",
    semReservas: "No substitutes available.",
    emCampo: "On the pitch",
    banco: "Bench",
    ninguemEmCampo: "Nobody on the pitch.",
    voltarParaOJogo: "Back to the match",

    pausar: "Pause",
    ritmo1x: "1x",
    ritmo2x: "2x",
    ritmo4x: "4x",
    ritmo10x: "10x",
    ritmo20x: "20x",
    verResumo: "See the matchday summary",
    pularParaResultado: "Skip to the result",
    condicaoDoJogador: "{condicao}%",
  },

  // -------------------------------------------------------------------------
  // Fim de temporada
  // -------------------------------------------------------------------------
  fimDeTemporada: {
    contextoDoCartao: "Season {temporada}",
    encerrada: "Season {temporada} is over",
    posicaoValor: "#{posicao}",
    naLiga: "in the {liga}",

    tituloConquistado: "Trophy won",
    titulosConquistados: "Trophies won",
    compartilharTitulo: "Share the title",

    conquistaNova: "Achievement unlocked",
    conquistasNovas: "Achievements unlocked",
    verNaCarreira: "The rest are on the Career screen.",

    // The board's verdict. It used to live in the save, as a paragraph frozen in
    // the wording of the day it was written, and is now put together here from
    // the verdict, the final position and the trophies.
    veredito: {
      demitido: "The board asked for this: {objetivo}. The team finished {posicao}th and the contract was not renewed.",
      titulo: "A trophy is an answer that needs no explaining. The board wants more of the same.",
      acima: "The board asked for this: {objetivo}. It got more than that, and your standing inside the club went up.",
      cumpriu: "The board asked for this: {objetivo}. {posicao}th will do. No party, no complaints.",
      abaixo: "The board asked for this: {objetivo}. {posicao}th fell short, and another season like it changes the conversation.",
    },
    oQueADiretoriaAchou: "What the board made of it",

    campanha: "Campaign",
    pontos: "Points",
    jogos: "Played",
    vitorias: "Wins",
    empates: "Draws",
    derrotas: "Losses",
    saldo: "Goal difference",
    meta: "The target was this: {meta}, which means finishing at position {posicao}.",

    caixaDaTemporada: "Season cash",
    receita: "Income",
    despesa: "Costs",
    resultado: "Net",
    caixaHoje: "Cash today",
    divida: "Debt",

    destaques: "Standouts",
    artilheiro: "Top scorer",
    artilheiroValor: "{nome}, {gols} goals",
    ninguemDestaque: "Nobody scored enough to stand out.",
    melhorNota: "Best rating",
    melhorNotaValor: "{nome}, {nota}",

    subiramDaBase: "Up from the youth team",
    garotos: "{total} kids",
    contratosAcabando: "Contracts running out",
    renoveAntes: "renew before they walk for free",
    renovar: "Renew",
    colunas: {
      pos: "Pos",
      jogador: "Player",
      idade: "Age",
      forca: "Strength",
      salario: "Wages",
    },

    quemChamou: "Who came calling",
    sondagens: "Approaches from other clubs",
    escolhaOndeRecomecar: "pick where to start again",
    aceitarTroca: "accepting swaps your club right now",
    ofertaLinha: "{liga} · {pais} · they want: {objetivo}",
    caixaDe: "cash of {valor}",
    assumir: "Take over",
    verClube: "View club",
    previaCaixa: "Cash",
    previaDivida: "Debt",
    previaFolha: "Wages per round",
    previaEstadio: "Stadium",
    previaPosicao: "Position",
    previaForca: "Squad strength",
    previaIdade: "Average age",
    previaElenco: "Squad ({total})",
    previaAnos: "{idade} yrs",

    semClube: "No club",
    ninguemChamou: "Nobody called this time. Start a new career whenever you like.",
    escolhaUmClube: "Pick a club to carry on with",
    comecarTemporada: "Start season {temporada}",
  },

  // -------------------------------------------------------------------------
  // Conta
  // -------------------------------------------------------------------------
  conta: {
    chaveDoAlfa: "Alpha access key",
    chaveDoAlfaAjuda: "In the LFM-XXXX-XXXX format.",
    erroEmail: "Invalid email.",
    erroCodigo: "Invalid code.",
    erroSenhaVazada: "This password has appeared in a public data breach. Choose another one.",
    confiraCampos: "Check: {campos}.",
    titulo: "Account",
    voltar: "Back to the game",

    /** Nome do aparelho na lista de dispositivos ativos. */
    aparelhos: {
      web: "Browser",
      android: "Android",
      ios: "iPhone or iPad",
      desktop: "Computer",
    },

    email: "Email",
    senha: "Password",
    novaSenha: "New password",
    nome: "Display name",
    codigo: "Code from the email",
    senhaAjuda: "At least {minimo} characters.",
    nomeAjuda: "From {minimo} to {maximo} characters. This is how you show up in leagues.",

    entrar: "Sign in",
    entrando: "Signing in...",
    criarConta: "Create account",
    criando: "Creating...",
    naoTenhoConta: "I do not have an account yet",
    jaTenhoConta: "I already have an account",
    esqueciASenha: "I forgot my password",
    enviarLink: "Send the link",
    enviando: "Sending...",
    redefinir: "Reset the password",
    jaTenhoCodigo: "I already have the code",
    voltarParaEntrar: "Back to sign in",

    aceite:
      "Creating an account means you accept the terms of use and the privacy policy, version {versao}.",
    contaCriada:
      "Account created. We sent an email to {email}. Confirm the address and then sign in.",
    linkEnviado: "If an account exists for that email, the recovery link is already on its way.",
    senhaTrocada: "Password changed. Sign in with the new one.",

    sairDaConta: "Sign out",
    emailNaoConfirmado:
      "Email not confirmed yet. Until it is, online play and purchases stay locked.",
    reenviarConfirmacao: "Send the confirmation again",
    confirmacaoReenviada: "Another link is on its way. Check your inbox.",
    contaDesde: "With us since {data}",

    perfil: "Profile",
    salvar: "Save",
    salvando: "Saving...",
    perfilSalvo: "Profile saved.",

    dispositivos: "Active devices",
    dispositivosAjuda:
      "Every device that signed in to this account shows up here. Do not recognise one? Drop it and change your password.",
    esteAparelho: "this device",
    ultimoUso: "last used {quando}",
    entrouEm: "signed in {quando}",
    derrubar: "Drop",
    semDispositivos: "No active devices.",

    direitos: "Entitlements and subscription",
    vitalicia: "Solo Lifetime Licence",
    passe: "Legacy Pass",
    temDireito: "Active",
    semDireito: "Not owned",
    renovaEm: "renews on {data}",
    semRenovacao: "no automatic renewal",
    porOrigem: "via {origem}",
    origens: {
      stripe: "card",
      pix: "Pix",
      play: "Google Play",
      appstore: "App Store",
      grant: "grant",
    },
    cosmeticos: "Cosmetics",
    cosmeticosTotal: "{total} item(s)",

    dados: "Your data",

    excluirTitulo: "Delete the account",
    excluirComoFunciona:
      "The account is marked for deletion and you lose access to online play straight away. For 7 days you can undo it by signing back in. On the seventh day we erase everything that can be erased.",
    excluirOQueSai:
      "What goes: email, name, password, avatar, sessions, notification tokens, your career, private datapacks, friendships and direct messages.",
    excluirOQueFica: "What stays, and why",
    excluirFicaFiscal: "Tax record of a purchase: a legal duty for 5 years.",
    excluirFicaLigas:
      "Results and tables of the leagues you played: they belong to everyone who took part, and your name becomes \"Manager removed\".",
    excluirFicaDatapacks:
      "Public datapacks you published: other people depend on them, so authorship goes anonymous. You can unpublish them first.",
    excluirFicaSancao: "Sanction record: while it lasts, so a ban cannot end with one click.",
    excluir: "Delete my account",
    excluirConfirmar: "Type {palavra} below to confirm.",
    excluirPalavra: "DELETE",
    excluirAgora: "Confirm deletion",
    exclusaoAgendada: "Deletion set for {data}. Until then, you can cancel it right here.",
    cancelarExclusao: "Cancel the deletion",
    exclusaoCancelada: "Deletion cancelled. Your account stays where it is.",
  },

  // -------------------------------------------------------------------------
  // Avisos no aparelho
  // -------------------------------------------------------------------------
  notificacoes: {
    titulo: "Alerts",
    ajuda:
      "Choose what is worth an alert on this device. Whatever you turn off here still shows up in your list inside the game.",

    /* Nome de cada tipo. As chaves são as mesmas que a API guarda. */
    tipos: {
      deadline: "Matchday deadline coming up",
      roundProcessed: "Matchday processed",
      offers: "Offers and auctions",
      chat: "Mentions in the league chat",
      marketing: "Legacy Football Manager news",
    },
    tiposAjuda: {
      deadline: "A reminder before the matchday closes, with time left to pick a lineup.",
      roundProcessed: "The result as soon as the matchday runs.",
      offers: "When someone bids for your player or beats your bid.",
      chat: "Only when someone writes your name.",
      marketing: "New features and new seasons. Never third-party ads.",
    },

    salvando: "Saving...",
    salvo: "Preferences saved.",

    /* O convite, no saguão da liga assíncrona. */
    convite: {
      titulo: "Want to know when the matchday runs?",
      corpo:
        "This league runs on its own, at the agreed time. Without an alert you can miss the lineup and only find out the result the next day.",
      sim: "Alert me",
      agoraNao: "Not now",
      pedindo: "Waiting for the browser...",
    },

    /* O que aparece depois da resposta do navegador. */
    ativados: "Done. You will get the matchday alert on this device.",
    negados: "No problem. You can turn it on later in the browser's notification settings.",
    indisponiveis: "This device does not take browser alerts.",
    semServidor: "This server has no device alerts set up.",
    falhou: "I could not turn the alerts on right now. You can try again later.",
    jaAtivos: "Alerts are on for this device.",
    bloqueados:
      "Alerts are blocked for the game in this browser. To get them back, allow them in the site settings.",
  },

  // -------------------------------------------------------------------------
  // Ligas online
  // -------------------------------------------------------------------------
  opcoes: {
    carreira: "Career",
    som: {
      titulo: "Sound",
      gol: "Goal sound in live matches",
    },
    titulo: "Options",
    voltar: "Back",

    tema: {
      titulo: "Theme",
      escuro: "Dark",
      claro: "Light",
      brasao: "Crest",
      semClube: "Available with a career open.",
    },
    idioma: {
      titulo: "Language",
      ajuda:
        "Applies to this device right away. If you have an account, the choice goes up to it and your next device opens like this.",
    },

    avisos: {
      precisaDeConta:
        "Alerts are stored in your account, one choice per person. Sign in to pick which ones you want to get.",
    },


    erros: {
      titulo: "Error reports",
      enviar: "Send a report when the game breaks",
      oQueVai:
        "It sends what broke, the game version, the platform, the world seed and the matchday number.",
      saveNuncaVai:
        "Your save is never sent. Seed and matchday are enough to reproduce the problem, because the simulation is deterministic.",
      semEndereco:
        "This build was made with nowhere to send reports, so there is nothing to send either way.",
    },


    sobre: {
      titulo: "About",
      versaoDoJogo: "Game version",
      versaoDoMotor: "Engine version",
      versaoDaApi: "API version",
      protocolo: "Protocol",
      motorDoServidor: "The server is running engine {versao}.",
      licencas: "Licences",
      licencaDoJogo:
        "Legacy Football Manager ships under a personal use licence. These are the open source libraries it loads along with it.",
    },
  },

  ligas: {
    titulo: "Online leagues",
    voltar: "Back",
    atualizar: "Refresh",
    carregarMais: "Load more",

    abas: {
      minhas: "My leagues",
      publicas: "Public leagues",
      criar: "Create league",
    },

    semMinhas: "You are not in any league yet. Join with an invite or start your own.",
    semPublicas: "No public league open right now.",

    entrarPorCodigo: "Join with an invite code",
    codigo: "Code",
    codigoAjuda: "6 to 12 letters and digits, as the host sent it.",
    entrar: "Join",
    entrou: "Done, you are in {liga}.",

    estados: {
      lobby: "In the lobby",
      running: "Running",
      paused: "Paused",
      finished: "Finished",
      stalled: "Stalled",
    },
    visibilidades: {
      private: "Private",
      public: "Public",
    },

    linha: {
      temporadaRodada: "Season {temporada} · matchday {rodada}",
      treinadores: "{humanos} of {max} managers",
      prazo: "Deadline in {quando}",
      semPrazo: "No matchday scheduled",
      meuClube: "Your club: {clube}",
      abrir: "Open",
    },

    criar: {
      titulo: "Create league",
      exigePasse: "Opening a league is part of the Legacy Pass.",
      nome: "League name",
      visibilidade: "Who can join",
      privada: "Only with the code",
      publica: "Anyone, from the showcase",
      maxHumanos: "Maximum managers",
      paises: "Countries",
      paisesAjuda: "From 1 to 8 countries. Each one brings the divisions picked below.",
      paisesNomes: {
        BR: "Brazil",
        AR: "Argentina",
        ES: "Spain",
        EN: "England",
        IT: "Italy",
        PT: "Portugal",
      },
      divisoes: "Divisions per country",
      divisoesOpcao: "{numero} division(s)",
      ritmo: "Pace",
      ritmoAsync: "By deadline",
      ritmoAsyncAjuda:
        "The matchday runs at the set time. Whoever sent no decision plays the previous lineup.",
      ritmoLive: "Live",
      ritmoLiveAjuda:
        "The matchday runs once everyone marks ready, within the maximum wait.",
      cron: "When the matchday runs",
      cronAjuda: "A five-field cron expression, in the chosen time zone.",
      fuso: "Time zone",
      esperaAoVivo: "Wait for the missing, in minutes",
      temporadas: "Seasons",
      distribuicao: "How clubs are handed out",
      distribuicaoSorteio: "Draw",
      distribuicaoDraft: "Draft",
      dificuldade: "Difficulty",
      dificuldades: {
        facil: "Easy",
        normal: "Normal",
        dificil: "Hard",
      },
      mercado: "Transfers between humans",
      mercadoLigado: "Allow deals between human managers",
      razaoMinima: "Minimum offer, against market value",
      razaoMinimaValor: "{valor}%",
      negociosPorDupla: "Deals per pair of clubs, per season",
      aprovacao: "The commissioner approves every deal",
      criar: "Create the league",
      criando: "Creating...",
      escolhaUmPais: "Pick at least one country.",
    },

    saguao: {
      membros: "Managers ({total} of {max})",
      comissario: "commissioner",
      semClube: "club to be set",
      online: "online",
      offline: "offline",
      pronto: "decision sent",
      semDecisao: "no decision this matchday",
      ultimaAcao: "last action {quando}",
      situacoes: {
        active: "active",
        left: "left",
        kicked: "kicked",
      },
      prazo: "Deadline",
      convite: "Invite code",
      copiar: "Copy",
      copiado: "Code copied.",
      gerarConvite: "Generate another code",
      conviteExpira: "Valid until {data}",
      comecar: "Start the league",
      comecando: "Starting...",
      comecarAjuda:
        "Only the commissioner starts it. From then on nobody else joins and the clubs are handed out.",
      sair: "Leave the league",
      expulsar: "Kick",
      pausar: "Pause",
      retomar: "Resume",
      forcarRodada: "Run the matchday now",
      rodadaPedida: "Matchday {rodada} requested. The server processes it in a moment.",
      abrirHub: "Enter the league",
      configuracao: "Setup",
      resumo:
        "{paises} · {divisoes} division(s) per country · {temporadas} season(s) · {dificuldade} difficulty",
      mercadoLigado: "Deals between humans on, minimum of {razao}% of the value.",
      mercadoDesligado: "No deals between human managers.",
    },
  },

  // -------------------------------------------------------------------------
  // Hub da liga online
  // -------------------------------------------------------------------------
  hubOnline: {
    assistir: "Watch the match",
    proximoJogo: "Next match",
    navAria: "League sections",
    sair: "Leave the league",
    nav: {
      elenco: "Squad",
      tabela: "Table",
      noticias: "News",
      treinadores: "Managers",
      chat: "Chat",
    },

    prazoRotulo: "Matchday deadline",
    semPrazo: "No matchday scheduled",
    prazoVencido: "Deadline passed. The matchday can run at any moment.",
    processando: "Matchday {rodada} is being processed. Decisions are locked.",
    pausada: "League paused by the commissioner.",
    encerrada: "League finished.",
    travada: "The league stalled and needs the commissioner.",
    avisoDePrazo: "{minutos} minute(s) to the deadline of matchday {rodada}.",

    temporadaRodada: "Season {temporada} · matchday {rodada}/{total}",
    caixa: "Cash",
    divida: "Debt",
    estadio: "Stadium",
    lugares: "{capacidade} seats",
    objetivo: "Target",

    semJogoMarcado: "No match scheduled this matchday.",
    emCasa: "Home",
    foraDeCasa: "Away",
    versus: "vs",

    decisoes: "Matchday decisions",
    prontos: "{prontos} of {total} ready",
    estouPronto: "Mark my decision as sent",
    aindaNao: "Unmark",
    decisoesAjuda:
      "At live pace, the matchday runs as soon as everyone marks ready. At deadline pace, the mark only tells the others you have sorted your side.",

    elenco: "Squad",
    elencoColunas: {
      numero: "No.",
      pos: "Pos",
      jogador: "Player",
      forca: "Str",
      moral: "Morale",
      fisico: "Fit",
      salario: "Wages",
    },
    lesionado: "injured {rodadas}",
    suspenso: "suspended",
    linhaSecundaria: "{idade} yrs · {destaques}",

    tabela: "Table",
    semTabela: "The table shows up once the first matchday runs.",
    noticias: "News",
    semNoticias: "Nothing new here.",
    noticiaRodada: "matchday {rodada}",
    treinadores: "Managers",

    chat: "League chat",
    chatVazio: "Nobody has said anything yet.",
    chatCampo: "Write a message",
    chatEnviar: "Send",
    chatDigitando: "someone is typing...",
    chatSemHistorico:
      "History is not available on this server. New messages show up here.",
    chatSistema: "League notice",
    chatDesconectado: "No connection to the league. The message only goes out when it is back.",

    resultado: "Matchday {rodada}: {casa} {golsCasa}-{golsFora} {fora}",
    posicaoAgora: "You are #{posicao} in the table.",
    atualizar: "Refresh",
    semClube: "You have no club in this league yet. For now you can only follow along.",
  },

  // -------------------------------------------------------------------------
  // Partida ao vivo pelo servidor
  // -------------------------------------------------------------------------
  partidaOnline: {
    aria: "Live league match",
    conectando: "Joining the match channel...",
    esperando: "Kick-off is coming.",
    minuto: "{minuto}'",
    fimDeJogo: "full time",
    desconectado: "Connection dropped. The match comes back when it does.",
    fechar: "Close",

    pausa: "Substitution break",
    janela: "window {janela} of 3",
    tempoParaDecidir: "{segundos}s to decide",
    semReservas: "Nobody on the bench.",
    emCampo: "On the pitch",
    banco: "Bench",
    ninguemEmCampo: "Nobody on the pitch.",
    trocaEnviada: "Substitution sent. It counts in the official matchday result.",
    seguirSemMexer: "Carry on without changes",
    semClubeNaPartida: "You are watching as a spectator, so you cannot make substitutions.",

    posse: "Possession",
    chances: "Chances",
    finalizacoes: "Shots",
    cartoes: "Cards",
    porQueAconteceu: "Why it happened",
  },

  // -------------------------------------------------------------------------
  // Editor de banco de dados
  // -------------------------------------------------------------------------
  editor: {
    titulo: "Database editor",

    acoes: {
      abrir: "Open file",
      gerar: "Generate database",
      branco: "Start blank",
      salvar: "Save file",
      jogar: "Play the round and follow the match",
      sair: "Leave the editor",
    },

    estado: {
      gerando: "Building a new database. It takes a few seconds.",
      semBancoTitulo: "No database open",
      semBancoCorpo:
        "Open a file you already have, generate a full database with all 320 clubs, or start blank and build your own from scratch.",
      aberto: "Opened in {ms} ms",
      /** Contagens do cabeçalho, cada uma com singular e plural. */
      contagem: {
        paisUm: "1 country",
        paisMuitos: "{total} countries",
        divisaoUm: "1 division",
        divisaoMuitos: "{total} divisions",
        clubeUm: "1 club",
        clubeMuitos: "{total} clubs",
        jogadorUm: "1 player",
        jogadorMuitos: "{total} players",
      },
      salvo: "File saved as {nome}",
    },

    erro: {
      arquivo: "I could not read this file. It has to be a database JSON.",
      semBanco: "Open or generate a database first.",
      invalido:
        "The database still has {total} problems. Fix them before starting a career, or the game will refuse the file.",
    },

    abas: {
      estrutura: "Structure",
      jogadores: "Players",
      massa: "Bulk",
    },

    arquivo: {
      titulo: "File",
      nome: "Database name",
      autor: "Author",
      notas: "Notes",
      notasAjuda: "Add where the data came from and what changed in this version.",
    },

    arvore: {
      titulo: "Countries and divisions",
      novoPais: "New country",
      novaDivisao: "New division",
      novoClube: "New club",
      clubeUm: "1 club",
      clubes: "{total} clubs",
    },
    /** Nome que um item novo recebe antes de alguém batizá-lo. Vira conteúdo do
     * arquivo, então nasce na língua de quem está editando. */
    padroes: {
      banco: "New database",
      pais: "New country",
      divisao: "Division",
      clube: "Club",
      estado: "State",
      copa: "Cup",
    },

    pais: {
      titulo: "Country",
      nome: "Name",
      sigla: "Code",
      siglaAjuda: "Up to four letters, unique in the file. It is what ties a player to a country.",
      adjetivo: "Adjective",
      adjetivoAjuda: "As in Brazilian. It shows up in news and national team text.",
      forca: "Country strength",
      forcaAjuda: "From 1 to 5. It weighs the country's football: prize money, crowds and fees.",
      copa: "Cup name",
      estados: "States",
      estadosAjuda: "One per line. A club with no state is left out of the state league.",
      divisoes: "Divisions",
      remover: "Remove country",
    },

    divisao: {
      titulo: "Division",
      nome: "Name",
      nivel: "Tier",
      nivelAjuda: "1 is the top flight. The tier orders promotion between the country's divisions.",
      acesso: "Promoted",
      acessoAjuda: "How many clubs go up to the division above at the end of the season.",
      queda: "Relegated",
      quedaAjuda: "How many clubs go down to the division below.",
      clubes: "Clubs in the division",
      remover: "Remove division",
    },

    clube: {
      titulo: "Club",
      nome: "Name",
      sigla: "Short name",
      siglaAjuda: "Three letters, as it shows on the crest and in the table.",
      fundacao: "Founded",
      cores: "Colours",
      corPrincipal: "Main colour",
      corSecundaria: "Second colour",
      reputacao: "Reputation",
      reputacaoAjuda:
        "From 1 to 6. It drives sponsorship, crowds and who agrees to play for the club.",
      torcida: "Fanbase",
      capacidade: "Stadium capacity",
      ingresso: "Ticket price",
      caixa: "Cash",
      estado: "Plays the state league",
      semEstado: "No state league",
      estruturas: "Facilities",
      elenco: "Squad",
      elencoResumo: "{jogadores} · {goleiros} · strength {forca}",
      goleiroUm: "1 goalkeeper",
      goleiroMuitos: "{total} goalkeepers",
      semGoleiro: "no goalkeeper",
      novoJogador: "New player",
      duplicar: "Duplicate club",
      mover: "Move division",
      moverAjuda: "Takes the club, with its whole squad, to another division of the same country.",
      remover: "Remove club",
    },

    estruturas: {
      stadium: "Stadium",
      youth: "Youth academy",
      training: "Training",
      medical: "Medical centre",
    },

    reputacoes: {
      um: "1 Local",
      dois: "2 State",
      tres: "3 Regional",
      quatro: "4 National",
      cinco: "5 Continental",
      seis: "6 World",
    },

    jogador: {
      titulo: "Player",
      nome: "Name",
      nacionalidade: "Nationality",
      nacionalidadeAjuda: "The country code, like BR. It sets which national team he belongs to.",
      posicao: "Position",
      idade: "Age",
      pe: "Foot",
      atributos: "Attributes",
      atributosAjuda:
        "The engine's fourteen. The percentage next to each name is its weight in the current position.",
      potencial: "Potential",
      potencialAjuda: "Strength ceiling. Never below today's strength.",
      personalidade: "Personality",
      lesao: "Injury proneness",
      salario: "Wages per matchday",
      contrato: "Contract years",
      forca: "Strength",
      forcaDerivada:
        "Strength is not edited. It comes out of the attributes the position weighs, by the same maths the engine uses, and it moves on its own when you change them.",
      voltar: "Back to the club",
      remover: "Remove player",
    },

    pes: {
      D: "Right",
      E: "Left",
      A: "Both",
    },

    personalidades: {
      lider: "Leader",
      profissional: "Professional",
      ambicioso: "Ambitious",
      temperamental: "Temperamental",
      mercenario: "Mercenary",
      tranquilo: "Easy-going",
    },

    lesoes: {
      baixa: "Low",
      media: "Medium",
      alta: "High",
    },

    jogadores: {
      titulo: "Players in the database",
      procurar: "Search by name",
      todasAsPosicoes: "All positions",
      todosOsPaises: "All countries",
      encontrados: "{total} of {geral}",
      vazio: "No player matches that filter.",
      colunas: {
        nome: "Name",
        pos: "Pos",
        idade: "Age",
        forca: "Strength",
        clube: "Club",
      },
    },

    massa: {
      titulo: "Bulk operations",
      renomear: "Rename in bulk",
      procurar: "Find",
      substituir: "Replace with",
      alvo: "Where to replace",
      escopo: "In which country",
      todosOsPaises: "All countries",
      previa: "{total} names would change",
      previaUm: "1 name would change",
      previaNenhum: "No name has that text",
      aplicar: "Replace all",
      aviso: "The search is case sensitive and replaces every occurrence inside the name.",
      feito: "{total} names changed.",
      feitoUm: "1 name changed.",
      alvos: {
        clubes: "Club names",
        jogadores: "Player names",
        ambos: "Clubs and players",
      },
      atalhos: "Duplicating and moving a club live on the club screen, with the rest of the editing.",
    },

    problemas: {
      titulo: "Problems",
      nenhum: "No problems. This database can start a career.",
      contagem: "{total} problems",
      um: "1 problem",
      ajuda: "Click a problem to open the item that needs fixing.",
      raiz: "file",
    },

    lugar: {
      pais: "Country {numero}",
      divisao: "Division {numero}",
      clube: "Club {numero}",
      jogador: "Player {numero}",
    },
  },

  // -------------------------------------------------------------------------
  // Ajuda dos atalhos de teclado
  // -------------------------------------------------------------------------
  atalhos: {
    aria: "Keyboard shortcuts",
    rotulo: "Keyboard",
    titulo: "Shortcuts",
    fecheComEsc: "close with Esc",
    explicacao:
      "The whole matchday fits on the keyboard. While you are typing in a field, no key is a shortcut: Esc leaves the field and gives the keyboard back to the game. With a panel open, only that panel's keys count.",
    emBreve: "coming soon",
    voltarAoJogo: "Back to the game",

    ligacaoAte: "to",

    grupos: {
      rodada: "Matchday",
      secoes: "Sections",
      navegacao: "Navigation",
      hotseat: "Hotseat",
      aoVivo: "In the live match",
      painelAberto: "With a panel open",
    },

    descricoes: {
      calendario: "Calendar",
      timeB: "B Team",
      relatorios: "Reports",
      jogar: "Play the matchday",
      elenco: "Squad",
      tatica: "Tactics",
      mercado: "Transfers",
      clube: "Club",
      competicoes: "Competitions",
      noticias: "News",
      financas: "Cash",
      carreira: "Career",
      busca: "Find a player: opens transfers with the cursor in the search box",
      lista: "Move through the list on screen",
      abrir: "Open the highlighted row",
      voltar: "Close the open panel, or go back to the squad",
      ajuda: "Show this list",
      assento: "Pass the turn to another hotseat manager",
      pausar: "Open or close the substitution, which pauses the match",
      ritmo: "Speed 1x, 2x, 4x, 10x or 20x",
      pular: "Close the substitution; at the end, open the summary",
      fecharPainel: "Close the top panel",
      continuar: "Continue on the match summary and at the end of the season",
      cartao: "Make the card to share",
    },
  },

  // -------------------------------------------------------------------------
  // Loja
  // -------------------------------------------------------------------------
  loja: {
    titulo: "Store",
    voltar: "Back",
    atualizar: "Refresh",
    carregarMais: "Load more",

    /*
     * Servidor sem credencial de provedor. Não é falha de quem está jogando,
     * é estado desta instalação, e o texto precisa dizer isso sem alarme.
     */
    semPagamento: "Purchases are not available on this server yet.",
    semPagamentoCorpo:
      "Whoever runs this server has not set up a payment method yet. The catalogue stays here to browse, and whatever you have already bought still counts.",
    semPagamentoMetodo: "The method asked for was {metodo}.",

    catalogo: "What you can buy",
    semProdutos: "This server has nothing in the catalogue for now.",

    grupos: {
      lifetime: "Lifetime licence",
      passe: "Legacy Pass",
      cosmetic: "Cosmetics",
      datapack: "Databases",
    },

    tipos: {
      lifetime: "One-off purchase",
      pass_month: "Monthly subscription",
      pass_year: "Yearly subscription",
      cosmetic: "Cosmetic item",
      datapack: "Database",
    },

    duracaoUmMes: "One month of access per charge",
    duracaoMeses: "{meses} months of access per charge",
    semPrazo: "No expiry date",
    semPreco: "No price in {moeda}",
    jaTem: "You already own this",
    restam: "{n} of {total} left",

    comprarCartao: "Pay by card",
    comprarPix: "Pay with Pix",
    abrindo: "Opening...",
    metodoFora: "This server does not offer payment by {metodo}.",
    metodos: {
      card: "card",
      pix: "Pix",
      android: "the Android store",
      ios: "the Apple store",
    },

    checkoutAberto: "We opened the payment page in another tab. Finish there and come back here.",
    checkoutSemAba: "The browser blocked the new tab. Use the button below to open the payment.",
    abrirPagamento: "Open the payment",
    fecharCobranca: "Close",

    pixTitulo: "Pix of {valor}",
    pixInstrucao: "Point the camera at the code, or use copy and paste.",
    pixCopiar: "Copy the code",
    pixCopiado: "Code copied.",
    pixExpira: "This code is valid until {data}.",
    pixEspera: "As soon as the payment lands, the entitlement shows up in your account on its own.",

    recibos: "Your receipts",
    semRecibos: "No purchase on record yet.",
    reciboLinha: "{situacao} · {origem} · {data}",
    situacoes: {
      pending: "waiting for payment",
      paid: "paid",
      refunded: "refunded",
      expired: "expired",
    },

    assinatura: "Subscription",
    assinaturaAtiva: "Legacy Pass active.",
    assinaturaRenova: "Renews on {data}.",
    assinaturaSemRenovacao: "No automatic renewal.",
    assinaturaSemPasse: "You have no active subscription to cancel.",
    cancelar: "Cancel the renewal",
    cancelando: "Cancelling...",
    cancelarAjuda:
      "Cancelling means it stops renewing, not that you lose what you paid for: the pass lasts to the end of the period.",
    cancelado: "Renewal cancelled on {total} subscription(s).",
    canceladoAte: "The pass still counts until {data}.",
    cancelarNaLoja:
      "This subscription was taken out inside the device's store, and that is where it ends. Open the store's subscriptions to finish.",

    restaurar: "Restore purchases",
    restaurando: "Checking...",
    restaurado: "Entitlements checked with the server.",
    restaurarAjuda:
      "Bought on another device, on the website or in a store? This reads this account's entitlements from the server again.",

    vestiario: "Your kit room",
    vestiarioAjuda:
      "Choose what to wear where. You can change it whenever you like, and changing costs nothing.",
    vestiarioVazio: "You have no cosmetics yet. Whatever you buy here shows up in this list.",
    vestiarioErro: "I could not load your kit room.",
    usar: "Wear",
    emUso: "In use",
    tirar: "Take off",
    trocando: "Changing...",
    trocado: "Done, the look has changed.",
    ondeAparece: {
      crest: "Shows on your club crest, in career mode and in online leagues.",
      kit: "Shows next to the crest, in your club header.",
      stadium: "Paints the club header in the stadium colours.",
      title: "Shows next to your name in the league's manager list.",
    },
  },

  // -------------------------------------------------------------------------
  // Cosméticos
  // -------------------------------------------------------------------------
  cosmeticos: {
    tipos: {
      crest: "Crest",
      kit: "Kit",
      stadium: "Stadium theme",
      title: "Manager title",
    },
    // Sigla que aparece dentro do escudo na vitrine, onde ainda não existe
    // clube nenhum para emprestar a dele.
    siglaDaPrevia: "LM",
    tituloAjuda: "A manager title, bought in the store. It changes nothing on the pitch.",
  },

  // -------------------------------------------------------------------------
  // Mercado entre treinadores humanos
  // -------------------------------------------------------------------------
  mercadoOnline: {
    titulo: "League market",
    voltar: "Back",
    atualizar: "Refresh",
    carregarMais: "Load more",

    semClube: "You have no club in this league, so there is nothing to trade.",
    mercadoDesligado: "This league does not allow deals between managers.",

    abas: {
      recebidas: "Received",
      enviadas: "Sent",
      leiloes: "Auctions",
      historico: "History",
    },

    filtros: {
      abertas: "Open",
      aguardando: "Waiting for the commissioner",
      todas: "All",
      leiloesAbertos: "Open",
      leiloesFechados: "Closed",
      leiloesTodos: "All",
    },

    situacoes: {
      open: "open",
      accepted: "accepted",
      rejected: "rejected",
      countered: "countered",
      expired: "expired",
      vetoed: "vetoed",
    },

    direcoes: {
      in: "came to you",
      out: "you offered",
    },

    semPropostas: "No offers here.",
    semLeiloes: "No auction here.",

    linhaValor: "Amount",
    linhaParcelas: "Installments",
    linhaPrazo: "Closes on matchday {rodada}",
    linhaPrazoVencido: "Deadline passed on matchday {rodada}",
    linhaTroca: "With {jogador} in the deal",
    linhaEmprestimo: "Loan until season {temporada}, with {percentual}% of the wages",
    linhaContraproposta: "The counter-offer asks for {valor}",
    naFila: "Already queued for this matchday",
    esperandoComissario: "Waiting for the commissioner's nod",

    aceitar: "Accept",
    recusar: "Reject",
    contrapor: "Counter",
    enviando: "Sending...",

    /*
     * A contraproposta troca os lados da mesa, e isso muda quem responde.
     * Quem não entende isso acha que contrapôs e ficou esperando o outro,
     * quando na verdade agora é ele quem está à espera.
     */
    contraporTitulo: "Counter: the table turns",
    contraporExplicacao:
      "The offer that came in is closed and one of yours is born, in the other direction. From here on the other manager is the one who answers, and the clock starts over.",
    contraporValor: "What you want",
    contraporEnviar: "Send the counter-offer",
    contrapostaEnviada:
      "Counter-offer sent. The original offer became {situacao}, and now it is the other manager who answers.",

    aceitaNaFila: "Deal done. The transfer goes into matchday {rodada}.",
    aceitaEsperando:
      "Deal done between the two of you. It still needs the commissioner's nod to enter the matchday.",
    recusadaOk: "Offer rejected.",

    rodada: "Matchday",
    situacaoDaLiga: "Status",
    avalDoComissario: "Commissioner's nod",
    avalExigido: "Required",
    avalDispensado: "Not required",

    aprovar: "Approve",
    aprovada: "Approved. The transfer goes into matchday {rodada}.",
    vetar: "Veto",
    vetoMotivo: "Reason for the veto, if you want to give one",
    vetoEnviar: "Confirm the veto",
    vetada: "Offer vetoed, and the transfer left the queue.",
    aprovacaoLigada:
      "In this league every deal goes past the commissioner before it enters the matchday.",

    leilaoLanceMinimo: "Minimum bid",
    leilaoMeuLance: "Your bid",
    leilaoSemLance: "You have not bid yet",
    leilaoClubes: "{total} club(s) in the running",
    leilaoFechado: "Closed at {valor}",
    leilaoSemVencedor: "Closed with no winner",
    leilaoLance: "What you pay",
    leilaoDarLance: "Place the bid",
    leilaoLanceFeito: "Bid of {valor} logged. It closes on matchday {rodada}.",
    leilaoFechadoAjuda:
      "Bids are blind: you see how many clubs came in and your own amount, never anyone else's. The winner is settled at the turn of the matchday.",

    novaTitulo: "Make an offer",
    novaJogador: "Player",
    novaEscolha: "Pick a player from another manager",
    novaBusca: "Search by name",
    novaSemJogadores: "No player from another manager is available right now.",
    naLista: "listed",
    novaValor: "What you pay",
    novaParcelas: "In how many installments",
    novaParcela: "{total}x",
    novaEnviar: "Send the offer",
    novaEnviada: "Offer sent to {clube}.",
    novaVirouLeilao:
      "Another club wants this player too, so the chase turned into an auction. Your offer went in as a bid.",
    novaPiso: "The floor in this league is {percentual}% of the player's value.",
  },

  // -------------------------------------------------------------------------
  // Ranqueado
  // -------------------------------------------------------------------------
  ranqueado: {
    titulo: "Ranked",
    voltar: "Back",
    atualizar: "Refresh",
    carregarMais: "Load more",

    temporada: "Season {numero}",
    temporadaSituacao: "Status",
    temporadaEstados: {
      upcoming: "not started yet",
      running: "running",
      finished: "finished",
    },
    temporadaPeriodo: "From {inicio} to {fim}",
    temporadaSemanas: "{semanas} weeks",
    temporadaTreinadores: "{total} managers per league",

    meuMmr: "Your MMR",
    minhaDivisao: "Your division",
    divisoes: "Divisions",
    divisaoFaixa: "from {mmr} MMR up",

    fila: "Queue",
    filaAjuda:
      "The league is built as soon as there are enough people with similar MMR. You can leave the queue at any time.",
    entrarNaFila: "Join the queue",
    entrando: "Joining...",
    sairDaFila: "Leave the queue",
    saindo: "Leaving...",
    naFila: "You have been in the queue since {quando}.",
    naFilaPosicao: "Position {posicao} of {total} in the {regiao} region.",
    naFilaFaltam: "{quantos} more to fill a league.",
    naFilaCompleta: "Enough people in the queue. The league goes out on the next join.",
    foraDaFila: "You are not in the queue.",
    saiuDaFila: "You left the queue.",
    regiao: "Region",

    ligaPronta: "Your ranked league is ready.",
    abrirLiga: "Open the league",
    emDisputa: "Season under way",
    minhaEntrada: "Division {divisao}, entry MMR {mmr}.",
    minhaPosicao: "Position {posicao} in the table.",
    semPosicaoAinda: "The final position comes at the end of the season.",
    semEntrada: "You are not playing a league this season yet.",

    /*
     * O ranqueado é do Passe, e o servidor recusa a fila com
     * `ENTITLEMENT_REQUIRED`. A tela não esconde o modo: mostra a temporada e o
     * leaderboard, e diz na hora certa o que falta.
     */
    exigePasse: "The ranked queue is part of the Legacy Pass.",
    irParaLoja: "See it in the store",

    leaderboard: "Leaderboard",
    leaderboardTodas: "All divisions",
    semLeaderboard: "Nobody has scored this season yet.",
    colunaPosicao: "#",
    colunaTreinador: "Manager",
    colunaMmr: "MMR",
    colunaDivisao: "Division",
    colunaNaLiga: "In the table",
    voce: "you",

    historico: "History",
    semHistorico: "No ranked season played yet.",
    historicoLinha: "Season {numero} · division {divisao}",
    historicoVariacao: "MMR from {inicio} to {fim}",
    historicoSemFim: "Entry MMR {inicio}, season still open",
    historicoPosicao: "Finished #{posicao}.",
  },



  // -------------------------------------------------------------------------
  // Text that comes from the engine
  // -------------------------------------------------------------------------
  jornal: {
    titulo: "News",
    mundo: "World",
    preTemporada: "Preseason",
    rodada: "Round {rodada}",
    edicao: "Season {temporada} · {quando}",
    vazio: "No news yet.",
    emAlta: "Trending",
    artilharia: "Top scorers",
    selecaoDaRodada: "Team of the round",
    mercado: "Season's transfers",
    semClube: "Free agent",
    verFicha: "{jogador}'s profile",
    editorias: {
      resultado: "Result",
      tecnicos: "Managers",
      mercado: "Transfers",
      lesoes: "Injuries",
      estadios: "Stadiums",
      numeros: "Numbers",
      selecao: "National team",
      financas: "Finances",
      competicao: "Competition",
      especulacao: "Rumour mill",
    },
    arte: {
      publico: "attendance",
      divida: "in debt",
      media: "per game",
    },
    fontes: {
      f1: "Gossip",
      f2: "Gossip",
      f3: "Reliable source",
      f4: "Official source",
    },
    selos: {
      confirmado: "Confirmed",
      desmentido: "Didn't happen",
    },
    noSeuClube: "At your club",
    efeitos: {
      moral: "Squad morale {delta}",
      moralOutro: "{clube} morale {delta}",
      diretoria: "Board patience {delta}",
      publico: "Attendance {delta}%",
      valor: "{jogador} value {delta}%",
      moralJogador: "{jogador} morale {delta}",
    },
  },

  sino: {
    titulo: "Notifications",
    vazio: "Nothing here.",
    abas: {
      mercado: "Transfers",
      elenco: "Squad",
      diretoria: "Board",
      competicao: "Competitions",
    },
  },

  coletiva: {
    rotulo: "Press conference",
    responder: "Answer",
    semComentarios: "No comment",
    perguntas: {
      vitoria: "Big result. Can {clube} dream big this season?",
      derrota: "Tough times. Is your job at risk?",
    },
    respostas: {
      vitoria: {
        r0: "One game at a time. The squad knows where it wants to go.",
        r1: "This is the best squad in the country, and today proved it.",
        r2: "They talked too much. We answered on the pitch.",
      },
      derrota: {
        r0: "The responsibility is mine. I'll find the way.",
        r1: "The players must deliver more, and they'll hear it from me.",
        r2: "It wasn't our day. The ball just wouldn't go in.",
      },
    },
    veiculos: {
      v0: "Capital Radio",
      v1: "Sport TV",
      v2: "City Gazette",
      v3: "Football Portal",
      v4: "National Radio",
      v5: "Fans Channel",
    },
  },

  motor: {
    manchetes: {
      especulacao: {
        monitora: {
          titulo: "{interessado} keeping tabs on {clube}'s {jogador}",
          fina: "{jogador} ({posicao}, {idade}) is valued at {valor}.",
          corpo: "Word is going around behind the scenes. {clube} have not commented.",
        },
      },
      coletiva: {
        vitoria: {
          humilde: {
            titulo: "{tecnico} plays it down: \"One game at a time\"",
            fina: "The {clube} manager spoke after the game.",
            corpo: "Despite the good run, {tecnico} kept his feet on the ground.",
          },
          confiante: {
            titulo: "\"Best squad in the country\": {tecnico} claims favourites' tag",
            fina: "The {clube} manager raised the stakes after the game.",
            corpo: "The words lift the squad, and the board now expects more.",
          },
          provocar: {
            titulo: "{tecnico} takes a swipe: \"They talked too much\"",
            fina: "The {clube} manager didn't spare the opponents.",
            corpo: "The fans loved it, and the rivals will remember it next time.",
          },
        },
        derrota: {
          culpa: {
            titulo: "{tecnico} takes the blame: \"The responsibility is mine\"",
            fina: "The {clube} manager shielded his players.",
            corpo: "The stance pleased the board, which is still behind the manager.",
          },
          cobrar: {
            titulo: "{tecnico} calls out his squad: \"They must deliver more\"",
            fina: "The {clube} manager didn't hide his frustration.",
            corpo: "The board backed the criticism; the dressing room felt it.",
          },
          azar: {
            titulo: "{tecnico} blames bad luck: \"It wasn't our day\"",
            fina: "The {clube} manager played down the result.",
            corpo: "The players liked the tone; the board, not so much.",
          },
        },
      },
      resultado: {
        viradaNoFim: {
          titulo: "{vencedor} complete late comeback against {perdedor}: {golsVencedor}-{golsPerdedor}",
          fina: "{autor} settled it in the {minuto}th minute.",
          corpo: "{competicao}: {vencedor} came from behind and found the winner in the closing minutes. {autor}'s goal in the {minuto}th minute sealed a {golsVencedor}-{golsPerdedor} win over {perdedor}.",
        },
        golNoFim: {
          titulo: "{minuto}th-minute winner: {vencedor} beat {perdedor} {golsVencedor}-{golsPerdedor}",
          fina: "{autor} scored the late winner.",
          corpo: "{competicao}: the game was heading for a draw until {autor} struck in the {minuto}th minute. {vencedor} beat {perdedor} {golsVencedor}-{golsPerdedor}.",
        },
        virada: {
          titulo: "{vencedor} turn it around to beat {perdedor} {golsVencedor}-{golsPerdedor}",
          fina: "{autor} scored the go-ahead goal in the {minuto}th minute.",
          corpo: "{competicao}: {perdedor} took the lead, but {vencedor} fought back. {autor} put them in front in the {minuto}th minute, and it finished {golsVencedor}-{golsPerdedor}.",
        },
        zebra: {
          titulo: "Upset: {vencedor} topple {perdedor} {golsVencedor}-{golsPerdedor}",
          fina: "{competicao}: the smaller club beat the favourite.",
          corpo: "Given the gap between the two clubs, it was the most unexpected result of the round. Final score: {vencedor} {golsVencedor}-{golsPerdedor} {perdedor}.",
        },
        goleada: {
          titulo: "{vencedor} thrash {perdedor} {golsVencedor}-{golsPerdedor}",
          fina: "{autor} scored {golsDoAutor} of the {golsVencedor} goals.",
          corpo: "{competicao}: {vencedor} dominated {perdedor} from start to finish. {autor} was the star of the show.",
        },
        classicoVitoria: {
          titulo: "{vencedor} win the derby against {perdedor} {golsVencedor}-{golsPerdedor}",
          fina: "{rivalidade} · {competicao}",
          corpo: "Derby day belonged to {vencedor}: {golsVencedor}-{golsPerdedor} over {perdedor}. For the fans, it is worth more than three points.",
        },
        classicoEmpate: {
          titulo: "Derby ends level: {mandante} {gols}-{gols} {visitante}",
          fina: "{rivalidade} · {competicao}",
          corpo: "{mandante} and {visitante} shared the points, and this time nobody takes the bragging rights home.",
        },
        novoLider: {
          titulo: "{clube} go top of the table",
          fina: "{competicao}: {pontos} points after round {rodada}.",
          corpo: "{clube} overtook {anterior}, who had led until the previous round.",
        },
        sequenciaDeVitorias: {
          titulo: "{clube} make it {vitorias} wins in a row",
          fina: "{competicao}: the team sits {colocacao} in the table.",
          corpo: "That is {vitorias} consecutive wins across all competitions.",
        },
      },
      tecnicos: {
        jejumNoZ4: {
          titulo: "{jogos} games without a win: {tecnico}'s {clube} drop into the relegation zone",
          fina: "{competicao}: the team sits {colocacao} in the table.",
          corpo: "The run piles pressure on {tecnico}. The board is watching closely.",
        },
        jejum: {
          titulo: "Pressure on {tecnico}: {clube} now {jogos} games without a win",
          fina: "{competicao}: the team sits {colocacao} in the table.",
          corpo: "The last win came {jogos} matches ago, across all competitions.",
        },
        troca: {
          titulo: "{clube} change manager: {saiu} out, {entrou} in",
          fina: "{saiu} leaves after finishing {colocacao} in season {temporada}.",
          corpo: "The board did not renew its faith in the project. {entrou} takes charge next season.",
        },
      },
      mercado: {
        transferencia: {
          titulo: "{para} sign {jogador} for {valor}",
          fina: "{jogador} ({posicao}, {idade}) leaves {de}.",
          corpo: "{de} receive {valor} for the sale.",
        },
        maiorVendaDaTemporada: {
          titulo: "Biggest sale of the season: {jogador} leaves {de} for {valor}",
          fina: "{jogador} ({posicao}, {idade}) signs for {para}.",
          corpo: "No deal this season had reached that fee until now.",
        },
        joiaVendida: {
          titulo: "{de} sell {idade}-year-old gem {jogador} for {valor}",
          fina: "The youngster ({posicao}) joins {para}.",
          corpo: "The club takes the money now; {para} bet on the future.",
        },
        contratacaoLivre: {
          titulo: "{para} sign {jogador} on a free",
          fina: "{jogador} ({posicao}, {idade}) was a free agent.",
          corpo: "Rated {forca}, he arrives without a transfer fee.",
        },
      },
      lesoes: {
        lesaoGrave: {
          titulo: "{jogador} injured and out for {rodadas} rounds for {clube}",
          fina: "{posicao} · goals this season: {gols}",
          corpo: "{clube} lose a key player: he is expected to miss {rodadas} rounds.",
        },
      },
      estadios: {
        recorde: {
          titulo: "{publico} fans: {clube} set a new stadium attendance record",
          fina: "The previous record was {anterior}. The stadium holds {capacidade}.",
          corpo: "The record crowd came for the game against {adversario}.",
        },
        mediaDePublico: {
          titulo: "{divisao}: {clube} outdraw {superados} top-flight clubs",
          fina: "An average of {media} fans per game, more than {superados} clubs in {divisaoDeCima}.",
          corpo: "The ranking counts this season's home games.",
        },
      },
      numeros: {
        marcaDeGols: {
          titulo: "{jogador} reaches {gols} goals for the season",
          fina: "{clube} · milestone reached in round {rodada}",
          corpo: "{jogador} is among the season's leading scorers.",
        },
        hatTrick: {
          titulo: "{jogador} scores {gols} as {clube} face {adversario}",
          fina: "Final score: {clube} {golsClube}-{golsAdversario} {adversario}",
          corpo: "The kind of night a striker never forgets.",
        },
      },
      selecao: {
        convocacao: {
          titulo: "{selecao} name a {total}-man squad for the Nations Cup",
          fina: "{jogador}, {idade}, is the youngest in the squad.",
          corpo: "The squad's youngest plays for {clube}.",
        },
        campea: {
          titulo: "{selecao} win the Nations Cup",
          fina: "The tournament returns in {temporadas} seasons.",
          corpo: "They are the best national team in the world until the next edition.",
        },
      },
      financas: {
        noVermelho: {
          titulo: "{clube} slip into the red",
          fina: "Cash stands at {caixa}, with {divida} of debt.",
          corpo: "With negative cash, the board may be forced to sell players to pay the bills.",
        },
      },
      competicao: {
        campeao: {
          titulo: "{competicao}: {clube} are champions",
          fina: "{pontos} points from {jogos} games in season {temporada}.",
          corpo: "The title crowns the campaign and brings the competition's top prize.",
        },
        campeaoDaCopa: {
          titulo: "{competicao}: {clube} lift the trophy",
          fina: "The title comes with the final's prize money.",
          corpo: "{clube} came through every knockout round to win it.",
        },
        acesso: {
          titulo: "{clube} win promotion",
          fina: "Next season, they play in {divisao}.",
          corpo: "It is the reward for a campaign near the top of the table.",
        },
        rebaixamento: {
          titulo: "{clube} are relegated",
          fina: "Next season, they play in {divisao}.",
          corpo: "The drop forces the club to start again a division lower, with less income.",
        },
      },
    },
    noticias: {
      resultado: {
        classicoVitoria: {
          titulo: "Derby win over {adversario}",
          corpo:
            "{rivalidade}. {mandante} {golsMandante}-{golsVisitante} {visitante} in the {torneio}. The fans will remember this win over {adversarioNome} for a few weeks.",
        },
        classicoVitoriaGrande: {
          titulo: "Derby win over {adversario}",
          corpo:
            "{rivalidade}. {mandante} {golsMandante}-{golsVisitante} {visitante} in the {torneio}. The fans will remember this win over {adversarioNome} for a long time.",
        },
        classicoEmpate: {
          titulo: "Derby draw with {adversario}",
          corpo:
            "{rivalidade}. {mandante} {golsMandante}-{golsVisitante} {visitante} in the {torneio}. Against {adversarioNome}, nobody walks away happy with a draw.",
        },
        classicoDerrota: {
          titulo: "Derby loss to {adversario}",
          corpo:
            "{rivalidade}. {mandante} {golsMandante}-{golsVisitante} {visitante} in the {torneio}. Losing to {adversarioNome} costs more than three points.",
        },
        classicoDerrotaGrande: {
          titulo: "Derby loss to {adversario}",
          corpo:
            "{rivalidade}. {mandante} {golsMandante}-{golsVisitante} {visitante} in the {torneio}. Losing to {adversarioNome} costs a great deal more than three points.",
        },
      },

      elenco: {
        suspensaoPorCartoes: {
          titulo: "Suspended on bookings",
          corpo: "{jogador} picked up a {amarelos}th yellow card and misses the next round.",
        },
        deVoltaDaSelecao: {
          titulo: "Back from international duty",
          corpo: "{jogador} is back from international duty and available again.",
        },
        climaPesado: {
          titulo: "Trouble in the dressing room",
          corpo:
            "{primeiro} and {segundo} argued in training. The squad has too many stars and no leader to hold the group together.",
        },
        pedidoDeSaida: {
          titulo: "Transfer request",
          corpo: "{jogador} asked to leave the club and is now on the transfer list.",
        },
        escalacaoRecusada: {
          titulo: "Lineup rejected",
          corpo: "The lineup was not accepted: {motivo}",
        },
        escalacaoAjustada: {
          titulo: "Lineup adjusted",
          corpo: "{saiu} could not play and {entrou} took the spot. The rest of the team and the formation stayed as you left them.",
        },
        escalacaoRefeita: {
          titulo: "Lineup rebuilt",
          corpo: "Your lineup could not field eleven, so the team went out with the automatic lineup. Check the tactics before the next round.",
        },
        conversaComOElenco: {
          titulo: "A word with the squad",
          corpo: "From the dressing room: {recado}",
        },
        renovacaoRecusada: {
          titulo: "Contract talks broke down",
          corpo: "{jogador} thought the offer was too low and turned the new deal down.",
        },
        dispensa: {
          titulo: "Released",
          corpo: "{jogador} was released. The pay-off cost {valor}.",
        },
        elogioAceito: {
          titulo: "A word with the squad",
          corpo: "{jogador} appreciated the praise.",
        },
        broncaMalRecebida: {
          titulo: "A word with the squad",
          corpo: "{jogador} did not take the dressing-down well at all.",
        },
        broncaAceita: {
          titulo: "A word with the squad",
          corpo: "{jogador} took the dressing-down and promised an answer on the pitch.",
        },
        garotoDeEmergencia: {
          titulo: "Youngster promoted in a hurry",
          corpo: "With the squad short of bodies, {jogador} was promoted from the academy.",
        },
        aposentadoria: {
          titulo: "Retirement",
          corpo:
            "{jogador} hung up his boots at {idade}, after {jogos} appearances and {gols} goals.",
        },
        basePedeDecisaoUm: {
          titulo: "The academy needs an answer",
          corpo:
            "{jogador} is old enough to step up. Promote him, keep him another year or release him before the season starts.",
        },
        basePedeDecisao: {
          titulo: "The academy needs answers",
          corpo:
            "{total} kids are old enough to step up: {jogadores}. Promote, keep for another year or release them before the season starts.",
        },
        basePerdeuGaroto: {
          titulo: "The academy lost a kid",
          corpo: "{jogador} turned {idade} without stepping up and went looking for football elsewhere.",
        },
        promovidoDaBase: {
          titulo: "Promoted from the academy",
          corpo: "{jogador} signed his first professional deal and now takes a squad place.",
        },
        dispensadoDaBase: {
          titulo: "Released from the academy",
          corpo: "{jogador} was released and is no longer part of the club.",
        },
        baseRecusou: {
          titulo: "The academy expected otherwise",
          corpo: "The call did not go through: {motivo}",
        },
        baseViraLivre: {
          titulo: "Left the B team",
          corpo: "{jogador} turned {idade} and joined the free agents.",
        },
        jogadorEstrela: {
          titulo: "Star Player!",
          corpo: "{jogador} came up from the B team as a Star Player. The squad's morale went up.",
        },
        vendidoDaBase: {
          titulo: "B team sale",
          corpo: "{jogador} was sold to {clube} for {valor}.",
        },
        tecnicoDaBaseContratado: {
          titulo: "New B team coach",
          corpo: "{tecnico} took over the B team. Paying off the previous coach cost {multa}.",
        },
      },

      lesao: {
        jogadorLesionado: {
          titulo: "Player injured",
          corpo: "{jogador} picked up an injury and is out for around {rodadas} round(s).",
        },
        recuperado: {
          titulo: "Back in training",
          corpo: "{jogador} is fit and ready to play.",
        },
      },

      financeiro: {
        obraConcluida: {
          titulo: "Building work finished",
          corpo: "The work is done and the facility is now at level {nivel}.",
        },
        obraEmAndamento: {
          titulo: "Work already under way",
          corpo: "Finish the current building work before starting another one.",
        },
        obraInvalida: {
          titulo: "Invalid upgrade",
          corpo: "Facilities go up one level at a time.",
        },
        obraInviavel: {
          titulo: "Upgrade out of reach",
          corpo: "The work costs {custo} and the club cannot cover it.",
        },
        emprestimoNegado: {
          titulo: "Loan refused",
          corpo: "The bank said no: {motivo}",
        },
      },

      diretoria: {
        vendaForcada: {
          titulo: "Forced sale",
          corpo:
            "With the books in the red, the board sold {jogador} for {valor} without asking the coaching staff.",
        },
        novoComando: {
          titulo: "New manager",
          corpo: "{treinador} takes over at {clube}. What the board wants: {objetivo}.",
        },
        bemVindoAoClube: {
          titulo: "Welcome to the club",
          corpo: "{treinador} takes charge of {clube}. The target for the season: {objetivo}.",
        },
        pressao: {
          titulo: "Pressure from the board",
          corpo:
            "The target: {objetivo}. The team sits {posicaoNaTabela}th and patience is running out.",
        },
        demissao: {
          titulo: "You have been sacked",
          corpo:
            "The board asked for this: {objetivo}. The team finished {posicao}th and your contract was not renewed.",
        },
      },

      mercado: {
        propostaDaMaquina: {
          titulo: "Offer from {clube}",
          corpo: "{clube} offered {valor} for {jogador}. Reply in Transfers.",
        },
        contratacaoLivre: {
          titulo: "Signing completed",
          corpo: "{jogador} ({posicao}, rated {forca}) joins {clube} on a free transfer.",
        },
        contratacaoAVista: {
          titulo: "Signing completed",
          corpo: "{jogador} ({posicao}, rated {forca}) joins {clube} for {valor}.",
        },
        contratacaoParcelada: {
          titulo: "Signing completed",
          corpo:
            "{jogador} ({posicao}, rated {forca}) joins {clube} for {valor} over {parcelas} instalments.",
        },
        vendaLivre: {
          titulo: "Player sold",
          corpo: "{jogador} left {vendedor} for {comprador} on a free transfer.",
        },
        vendaAVista: {
          titulo: "Player sold",
          corpo: "{jogador} left {vendedor} for {comprador} in a {valor} deal.",
        },
        vendaParcelada: {
          titulo: "Player sold",
          corpo:
            "{jogador} left {vendedor} for {comprador} in a {valor} deal over {parcelas} instalments.",
        },
        emprestimoFechado: {
          titulo: "Loan agreed",
          corpo:
            "{jogador} ({posicao}, rated {forca}) plays for {clube} until the end of the season, and the club covers {parte}% of the wages.",
        },
        jogadorEmprestado: {
          titulo: "Player loaned out",
          corpo:
            "{jogador} ({posicao}, rated {forca}) goes on loan to {clube} until the end of the season, with {parte}% of the wages covered there.",
        },
        fimDeEmprestimo: {
          titulo: "Loan spell over",
          corpo: "{jogador} returns to {dono} after a season at {emprestador}.",
        },
        relatorioDeOlheiro: {
          titulo: "Scout report",
          corpo: "{jogador} was watched. Potential somewhere between {minimo} and {maximo}. {veredito}",
        },
        propostaExpirada: {
          titulo: "Bid expired",
          corpo: "The bid for {jogador} ran out with no answer.",
        },
        disputaVencida: {
          titulo: "Bidding war won",
          corpo: "{clubes} clubs were chasing {jogador}. The highest bid took him.",
        },
        janelaFechada: {
          titulo: "Window closed",
          corpo: "The transfer window is shut this round.",
        },
        caixaInsuficiente: {
          titulo: "Not enough cash",
          corpo: "The {entrada} down payment is more than the club has.",
        },
        emprestimoRecusado: {
          titulo: "Loan turned down",
          corpo: "The loan fell through: {motivo}",
        },
        olheiroParado: {
          titulo: "Scout stood down",
          corpo: "The scout never went out: {motivo}",
        },
        contratoEncerrado: {
          titulo: "Contract expired",
          corpo: "{jogador} left {clube} at the end of his deal and is a free agent.",
        },
      },

      competicao: {
        gruposEncerrados: {
          titulo: "{competicao} group stage is over",
          corpo: "{clubes} clubs are still alive in the hunt for the continental title.",
        },
        campeaoDaCopa: {
          titulo: "{clube} are champions",
          corpo: "{clube} have won the {competicao}.",
        },
        temporadaComeca: {
          titulo: "Season {temporada} kicks off",
          corpo: "It is {rodadas} rounds until we know the champions.",
        },
        campeaoDaLiga: {
          titulo: "{clube} win the {competicao}",
          corpo: "With {pontos} points from {jogos} games, {clube} are champions of season {temporada}.",
        },
        acesso: {
          titulo: "Promotion won",
          corpo: "{clube} go up to the {divisao}.",
        },
        rebaixamento: {
          titulo: "Relegation",
          corpo: "{clube} drop down to the {divisao}.",
        },
        nacoesJogo: {
          titulo: "Nations Cup: {mandante} {golsMandante} x {golsVisitante} {visitante}",
          corpo: "In the {fase}, {vencedor} go through.",
        },
        nacoesJogoNosPenaltis: {
          titulo: "Nations Cup: {mandante} {golsMandante} x {golsVisitante} {visitante}",
          corpo: "In the {fase}, it went to penalties. {vencedor} go through.",
        },
        nacoesCampea: {
          titulo: "{selecao} win the Nations Cup",
          corpo: "The trophy goes to the national side. The tournament returns in {temporadas} seasons.",
        },
      },
    },

    rotulos: {
      rivalidade: {
        historica: "Historic rivalry",
        regional: "Local derby",
        recente: "Recent rivalry",
      },
      olheiro: {
        vaiCrescer: "Plenty of room to grow, and worth the gamble.",
        quaseFeito: "Still improving, but close to the finished article.",
        noTeto: "He has hit his ceiling. What you see is what you get.",
      },
      reputacao: {
        "1": "Local",
        "2": "State",
        "3": "Regional",
        "4": "National",
        "5": "Continental",
        "6": "Worldwide",
      },
      objetivo: {
        titulo: "Win the title",
        brigarPeloTitulo: "Fight for the title",
        topQuatro: "Finish in the top four",
        acesso: "Win promotion",
        meioDeTabela: "Finish mid-table",
        fugirDoRebaixamento: "Avoid relegation",
      },
      nacoes: {
        final: "final",
        semifinal: "semi-final",
        quartas: "quarter-final",
        oitavas: "round of 16",
        faseInicial: "opening round",
      },
      // A fase de um mata-mata de clubes. A chave é o identificador que o save
      // guarda em `Fixture.stage`; o nome escrito vive aqui.
      fase: {
        preliminar: "Preliminary round",
        inicial: "Opening round",
        primeira: "First round",
        segunda: "Second round",
        oitavas: "Round of 16",
        quartas: "Quarter-finals",
        semifinal: "Semi-finals",
        final: "Final",
        grupo: "Group {letra}",
        deClubes: "Round of {clubes}",
      },
      estrutura: {
        stadium: "Stadium",
        youth: "Youth academy",
        training: "Training ground",
        medical: "Medical department",
      },
    },

    // The match commentary, moment by moment. It is the text a player reads most
    // of all: it runs live and comes back in full in the report.
    lances: {
      golArmacao: "{jogador} finishes off a worked move.",
      golArmacaoAssistido: "{jogador} finishes off a worked move, set up by {assistente}.",
      golDrible: "{jogador} beats his marker and scores.",
      golDribleAssistido: "{jogador} beats his marker and scores, set up by {assistente}.",
      golCruzamento: "{jogador} rises in the box and heads it home.",
      golCruzamentoAssistido: "{jogador} heads home a cross from {assistente}.",
      golContraAtaque: "{jogador} finishes off the counter.",
      golContraAtaqueAssistido: "{jogador} finishes off the counter, set up by {assistente}.",
      golBolaParada: "{jogador} scores from a set piece.",
      golPenalti: "{jogador} converts the penalty.",
      penaltiPerdido: "{jogador} wastes the penalty.",
      defesa: "{goleiro} saves the shot from {jogador}.",
      paraFora: "{jogador} shoots wide.",
      vermelhoDireto: "{jogador} is sent off on the spot.",
      segundoAmarelo: "{jogador} picks up a second booking and is sent off.",
      amarelo: "{jogador} goes into the book.",
      lesao: "{jogador} pulls up injured and needs treatment.",
      substituicao: "{entra} comes on for {sai}.",
      posse: "Possession.",
      falta: "{jogador} fouls {sobre}.",
      impedimento: "{jogador} is caught offside.",
      decisaoPorPenaltis:
        "Decided on penalties: {mandante} {golsMandante} x {golsVisitante} {visitante}.",
    },

    // Why the engine turned an order down. It travels two ways: straight to the
    // screen when the click just happened, and as the `{motivo}` of a news item
    // when the order came from the decision queue.
    motivos: {
      escalacao: {
        faltamTitulares: "The lineup needs 11 starters.",
        jogadorRepetido: "One player is named twice in the lineup.",
        jogadorDesconhecido: "There is an unknown player in the lineup.",
        deOutroClube: "One of the players named is not at this club.",
        lesionado: "{jogador} is injured.",
        suspenso: "{jogador} is suspended.",
        naSelecao: "{jogador} is away with his country and cannot play for now.",
        semGoleiro: "Name at least one goalkeeper.",
        titularNoBanco: "A starter is also on the bench.",
        reservaInvalido: "One of the substitutes is not at this club.",
        bancoCheio: "The bench takes {maximo} players at most.",
      },
      emprestimo: {
        jogadorDesconhecido: "That player does not exist.",
        jogadorLivre: "A free agent is signed, not borrowed.",
        jaEmprestado: "He is already out on loan.",
        jaEDoClube: "He is already one of yours.",
        clubeDesconhecido: "That club does not exist.",
        elencoDoDonoCurto: "{clube} would be left far too short.",
        elencoCheio: "Your squad is already full.",
        contratoAcabando: "His contract runs out this season.",
        salarioInsuficiente: "{clube} want you to cover at least {parte}% of his wages.",
      },
      base: {
        garotoSumiu: "That youngster is no longer in the academy.",
        elencoCheio: "The squad is already at the {maximo} player limit.",
        idadeNoLimite: "{jogador} cannot stay in the academy past {idade}.",
        semEssaProposta: "That offer is no longer on the table.",
        compradorSemVaga: "{clube} has no room left in the squad.",
        janelaFechada: "The transfer window is closed.",
        tecnicoIndisponivel: "That coach is no longer available.",
        caixaNaoCobreMulta: "The club cannot afford the {multa} payoff.",
      },
      observacao: {
        jogadorDesconhecido: "That player does not exist.",
        doProprioElenco: "You do not need a scout for your own squad.",
        jaEntregue: "Your scout already filed that report this season.",
        semCaixa: "Watching that player costs {custo}.",
      },
      banco: {
        valorInvalido: "Invalid amount.",
        limiteDoBanco: "The bank will lend {limite} at most.",
      },
      emprego: {
        treinadorDesconhecido: "Manager not found.",
        soHumanoTroca: "Only a human manager changes clubs here.",
        foraDaJanela: "Changing clubs only happens between seasons.",
        clubeDesconhecido: "Club not found.",
        semProposta: "That club has not made you an offer.",
        clubeOcupado: "That club already has a manager.",
      },
    },

    // B team achievements, on the youngster's card.
    feitos: {
      artilheiro: "Reserve League top scorer {temporada} · {gols} goals in {jogos} games",
      campeao: "Reserve League champion {temporada}",
      melhorEmCampo: "Man of the match · {vezes}× in {temporada}",
      hatTrick: "Hat-trick against {adversario} B · {temporada}",
      gols: "{gols} goals in {jogos} games · {temporada}",
      assistencias: "{assistencias} assists · {temporada}",
      semSofrerGol: "{jogos} clean sheets · {temporada}",
      selecaoDaRodada: "Team of the matchday · {vezes}× in {temporada}",
    },

    // The loose notes of a scout report, one per line.
    laudo: {
      lider: "Leads the dressing room.",
      profissional: "A model professional day to day.",
      ambicioso: "Ambitious: wants to play and wants to grow.",
      temperamental: "Hot-headed, and it shows on the pitch.",
      mercenario: "A mercenary: he follows the best wage.",
      tranquilo: "Easy-going, never a problem for the group.",
      lesoesPreocupam: "His injury record is a worry.",
      fisicoNaMedia: "Physically about average.",
      raramenteSeMachuca: "He is rarely injured.",
      idadeAFavor: "Age on his side: there is time to develop him.",
      contratoPertoDoFim: "Contract near its end. There is room to haggle.",
    },

    // Nome e descrição de cada conquista de carreira. O motor manda a chave; o
    // identificador dela, que é o que o save guarda, nunca aparece na tela.
    conquistas: {
      primeiroTitulo: { nome: "First silverware", descricao: "Lift the first trophy of your career." },
      campeaoNacional: { nome: "National champion", descricao: "Win a country's top division." },
      donoDoEstado: { nome: "King of the state", descricao: "Win a state championship." },
      campeaoDaCopa: { nome: "Cup football is another game", descricao: "Win a national cup." },
      campeaoContinental: { nome: "Across the continent", descricao: "Win the continental competition." },
      invicto: { nome: "Nobody got past", descricao: "Finish a league season without losing a single match." },
      contasEmDia: { nome: "Books in order", descricao: "End the season in profit and owing the bank nothing." },
      goleador: { nome: "A striker worth the name", descricao: "Have a player score 25 goals or more in one season." },
      superouAMeta: { nome: "Above the deal", descricao: "Finish well above what the board asked for." },
      tricampeao: { nome: "Three in a row", descricao: "Win the same competition three times." },
      dinastia: { nome: "Dynasty", descricao: "Collect ten trophies over your career." },
      casaEComida: { nome: "Bed and board", descricao: "Complete five straight seasons at the same club." },
      andarilho: { nome: "Stamped passport", descricao: "Manage clubs in three different countries." },
      doFundoDoPoco: { nome: "Up from the bottom", descricao: "Win promotion with a club outside the top flight." },
    },

    // O que há de errado num banco de dados, para quem o editou.
    datapack: {
      naoEDatapack: "This file is not a datapack.",
      formatoDiferente: "This game reads datapacks in format {esperado}, and the file says {encontrado}.",
      semPais: "A datapack needs at least one country.",
      siglaDoPais: "A country needs a code of up to {maximo} letters.",
      siglaRepetida: "The code {sigla} shows up twice.",
      semNomeDoPais: "The country has no name.",
      semDivisao: "A country needs at least one division.",
      divisaoCurta: "A division needs at least {minimo} clubs to have a fixture list.",
      divisaoImpar: "A division with an odd number of clubs leaves someone without a match every round.",
      semNomeDoClube: "The club has no name.",
      elencoCurto: "{clube} needs at least {minimo} players to field a side.",
      semGoleiro: "{clube} has no goalkeeper.",
    },

    save: {
      semEsquema: "This file is not a Legacy Football Manager save.",
      deVersaoMaisNova:
        "This save was written by a newer version of the game (schema {esquema}). Update the app to open the career.",
      semMigracao:
        "This save is on schema {esquema} and the game does not know how to update it. It may have been edited by hand.",
    },

    cartaoDeContratacao: {
      semCusto: "Free transfer",
      aVista: "For {valor}",
      parcelado: "For {valor} over {parcelas} instalments",
    },

    fatores: {
      posse: "{clube} ran the midfield, with {posse}% of the ball.",
      eficiencia:
        "{melhor} were sharper: {golsDoMelhor} goal(s) from {chancesDoMelhor} chances, against {golsDoPior} from {chancesDoPior} for {pior}.",
      decisivo: "{jogador} settled it with {gols} goals.",
      expulsao: "The sending-off on {minuto} minutes left {clube} a man down.",
      setor: "The {forte} attack was clearly better than the {fraco} defence.",
      cansaco: "{jogadores} players started the match below 55% fitness.",
      truncado: "A scrappy match, with barely a chance created at either end.",
      semGol: "Plenty of chances and no goals: {chances} openings wasted in total.",
      equilibrado: "Even from first minute to last, with neither side able to take over.",
      detalhes: "A tight match, settled by small margins in favour of {clube}.",
    },
  },

  // -------------------------------------------------------------------------
  // API errors, picked by the stable code rather than by the server message
  // -------------------------------------------------------------------------
  errosDaApi: {
    VALIDATION: "Something in the form is not what we expected. Check what you typed.",
    UNAUTHENTICATED: "Your session is over. Sign in again to carry on.",
    FORBIDDEN: "Your account is not allowed to do that.",
    NOT_FOUND: "We could not find what you asked for.",
    EMAIL_TAKEN: "There is already an account with that email.",
    ALPHA_KEY_INVALID: "That access key does not exist or has already been used.",
    DEVICE_LIMIT: "This account has activated too many devices in the last 30 days.",
    INVALID_CREDENTIALS: "That email and password do not match.",
    EMAIL_NOT_VERIFIED: "Confirm your email before signing in.",
    TOKEN_EXPIRED: "Your session expired. Sign in again.",
    ENTITLEMENT_REQUIRED: "That is part of something your account does not have yet.",
    LEAGUE_FULL: "That league is already full.",
    LEAGUE_STARTED: "That league has already started and is not taking anyone else.",
    DEADLINE_PASSED: "The round deadline has passed. Your orders count for the next one.",
    ROUND_PROCESSING: "The round is being played out. Try again in a moment.",
    NOT_MEMBER: "You are not in that league.",
    NOT_COMMISSIONER: "Only the league commissioner can do that.",
    INVALID_LINEUP: "That lineup is not valid.",
    WINDOW_CLOSED: "The transfer window is shut.",
    INSUFFICIENT_FUNDS: "The club cannot cover that amount.",
    OFFER_BELOW_MINIMUM: "The bid is below what the owner will take.",
    TRADE_COOLDOWN: "Wait a while before dealing with that club again.",
    PLAYER_UNAVAILABLE: "That player is not available right now.",
    RATE_LIMITED: "That is too many requests in a short time. Give it a moment.",
    ENGINE_VERSION_MISMATCH: "This app is out of date. Update it to carry on.",
    CONFLICT: "Someone changed this before you did. Reload and try again.",
    PAYLOAD_TOO_LARGE: "The file is bigger than we accept.",
    BILLING_NOT_CONFIGURED: "The store is off the air for now. Try again later.",
    LINKED_ACCOUNT: "Another account linked to this one is already in that league.",
    NATIONS_WINDOW_CLOSED: "Call-ups only open in a Nations Cup year.",
    TOTP_SETUP_REQUIRED: "Set up the second factor to continue.",
    TOTP_REQUIRED: "Enter the code from your authenticator app.",
    TOTP_INVALID: "That code does not match.",
    EMAIL_NOT_CONFIGURED: "This server does not send e-mail yet.",
    REDE: "We could not reach the server. Check your connection.",
    INTERNAL: "Something went wrong on the server. Try again in a moment.",
  },
} satisfies Dicionario;
